import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Plus, Users, RefreshCcw, Pencil, Phone, Mail, Building2, Building, X, Sparkles, Zap, FolderKanban, MessageSquare, PhoneCall, Calendar, ClipboardList, Mic, Download, Copy, Bug, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, FileText, FileSpreadsheet, Activity, Bell, Repeat, Archive, ArchiveRestore } from 'lucide-react';
import * as XLSX from 'xlsx';
import PhoneInput from '@/components/PhoneInput';
import { LeadLogWA } from '@/components/LeadLogWA';
import { LimitUpgradeDialog } from '@/components/LimitUpgradeDialog';
import DebugAssignmentsPanel from '@/components/DebugAssignmentsPanel';
import AiHealthSimplePanel from '@/components/AiHealthSimplePanel';
import RadiografiaPanel from '@/components/RadiografiaPanel';
import HelpTip from '@/components/HelpTip';
import RegisterActivityDialog from '@/components/activities/RegisterActivityDialog';
import ActivitiesBellDialog from '@/components/activities/ActivitiesBellDialog';
import useIsMobile from '@/hooks/useIsMobile';
import VerSuspendidos from '@/components/VerSuspendidos';
import { vendorsVisibleForGroup, groupsVisibleForVendor, memberIdsOfGroup, byFacet, campaignsVisibleForGroup, groupsVisibleForCampaign } from '@/lib/filterCascade';
import { isDupLead, frequencyOf, markDuplicatesInList } from '@/lib/leadDuplicate';
import { DEFAULT_CEM_RATINGS, ratingLabel, ratingColorClass } from '@/lib/cemRatings';
import FilterField from '@/components/filters/FilterField';
import useChartExport from '@/hooks/useChartExport';

// dashboard-pipeline P0-D: 'prospecto' es el status real de entrada (~93% de los leads
// en PROD) — faltaba en la UI, así que casi ningún lead mostraba badge/filtro coherente.
const STATUSES = DEFAULT_CEM_RATINGS.map(r => r.id);
// leads-sin-asignar-por-baja-vendedor: mismo tope que bulk_reassign_leads (routes/leads.py) --
// "Seleccionar todos" no puede tildar mas de lo que el backend va a aceptar.
const BULK_REASSIGN_MAX = 200;
const SOURCES = ['manual', 'webhook', 'landing', 'whatsapp', 'campaña'];
// leads-sin-asignar-por-baja-vendedor: texto amigable para reassignment_history.source.
// Conjunto real confirmado por aggregate en LAB+PROD (2026-08-24) -- 6 valores, no solo
// update_lead/bulk_reassign. Un source futuro no mapeado cae al default 'Reasignación'
// (NUNCA se pinta el string crudo, Principio #9).
const REASSIGNMENT_SOURCE_LABELS = {
  update_lead: 'Reasignación manual',
  bulk_reassign: 'Reasignación masiva',
  bulk_reassign_split: 'Reasignación pareja masiva',
  awaiting_schedule_release: 'Liberado al abrir el horario del equipo',
  awaiting_schedule_admin_fallback: 'Asignado a un administrador (el equipo no abrió a tiempo)',
  awaiting_development_admin_fallback: 'Asignado a un administrador (no se identificó el desarrollo de interés a tiempo)',
  ai_agent_awaiting_development_resolved: 'Asignado por el Agente IA al identificar el desarrollo de interés',
};
const getReassignmentSourceLabel = (source) => REASSIGNMENT_SOURCE_LABELS[source] || 'Reasignación';
const statusColors = {
  nuevo: 'status-nuevo', prospecto: 'status-prospecto', contactado: 'status-contactado', calificado: 'status-calificado',
  negociacion: 'status-negociacion', ganado: 'status-ganado', perdido: 'status-perdido'
};

// WS-1.5: Mapa unificado de estados de llamada
const CALL_STATUS_MAP = {
  completed:         { label: 'Conectada',            dot: 'bg-green-500',  text: 'text-green-400'  },
  vendor_no_answer:  { label: 'Vendedor no contestó', dot: 'bg-red-500',    text: 'text-red-400'    },
  vendor_voicemail:  { label: 'Buzón del vendedor',   dot: 'bg-orange-500', text: 'text-orange-400' },
  lead_canceled:     { label: 'Lead colgó antes',     dot: 'bg-yellow-500', text: 'text-yellow-400' },
  lead_no_answer:    { label: 'Lead no contestó',     dot: 'bg-gray-400',   text: 'text-gray-300'   },
  initiated:         { label: 'Iniciada',             dot: 'bg-blue-500',   text: 'text-blue-400'   },
  ringing:           { label: 'Sonando',              dot: 'bg-blue-500',   text: 'text-blue-400'   },
  in_progress:       { label: 'En curso',             dot: 'bg-blue-500',   text: 'text-blue-400'   },
  connecting_vendor: { label: 'Conectando asesor',    dot: 'bg-blue-500',   text: 'text-blue-400'   },
  failed:            { label: 'Falló',                dot: 'bg-red-500',    text: 'text-red-400'    },
  no_answer:         { label: 'Sin respuesta',        dot: 'bg-gray-400',   text: 'text-gray-300'   },
  answered:          { label: 'Lead contestó',        dot: 'bg-blue-500',   text: 'text-blue-400'   },
  connected:         { label: 'Conectada',            dot: 'bg-green-500',  text: 'text-green-400'  },
};

const ORIGIN_BADGE = {
  ai_agent: { label: 'IA',     icon: '🤖', cls: 'bg-purple-500/20 text-purple-300 border-purple-500/40' },
  auto:     { label: 'Auto',   icon: '🔄', cls: 'bg-blue-500/20 text-blue-300 border-blue-500/40'       },
  manual:   { label: 'Manual', icon: '👤', cls: 'bg-zinc-500/20 text-zinc-300 border-zinc-500/40'       },
};

const twilioCallLink = (sid) =>
  `https://console.twilio.com/us1/monitor/logs/calls?frameUrl=${encodeURIComponent(
    `/console/voice/calls/logs/${sid}?x-target-region=us1&__override_layout__=embed&bifrost=true`
  )}`;

const formatCallDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('es-MX', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch (e) {
    return '—';
  }
};

// Fix 6.5: helpers para vendedor reasignado + tiempo de reaccion, calculados
// directo de lead.reassignment_history (ya viene en el documento del lead,
// sin consulta extra por fila).
const _reactionSeconds = (lead) => {
  const hist = lead?.reassignment_history;
  if (!Array.isArray(hist) || hist.length === 0 || !lead?.created_at) return null;
  const first = hist[0];
  try {
    const delta = (new Date(first.at) - new Date(lead.created_at)) / 1000;
    return delta >= 0 ? delta : null;
  } catch (e) {
    return null;
  }
};
const _formatDuration = (seconds) => {
  if (seconds == null) return '—';
  if (seconds < 60) return `${Math.round(seconds)}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}min`;
  if (seconds < 86400) return `${(seconds / 3600).toFixed(1)}h`;
  return `${(seconds / 86400).toFixed(1)}d`;
};
const _lastReassignment = (lead) => {
  const hist = lead?.reassignment_history;
  if (!Array.isArray(hist) || hist.length === 0) return null;
  const last = hist[hist.length - 1];
  // from_vendor vacio/null = fue la asignacion de nacimiento (nace sin
  // vendedor previo), no una reasignacion real -- no se muestra el badge.
  // CORRECCION (2026-08-26, bug real de LAB, lead "Jose Martinez"): el
  // criterio viejo usaba la POSICION en el array (length < 2 = "solo
  // nacimiento"), asumiendo que la entrada 0 siempre es el nacimiento. Un
  // lead cuya asignacion de nacimiento nunca paso por push_reassignment_
  // history (ej. creado con assigned_to ya puesto) puede tener una UNICA
  // entrada de historial que YA es una reasignacion real -- el criterio por
  // longitud la descartaba siempre. El campo from_vendor de la ULTIMA
  // entrada (vacio solo en un nacimiento) es el mismo criterio que usa el
  // backend (es_reasignacion_real), independiente de la posicion.
  return last?.from_vendor ? last : null;
};

const getCallStatusCfg = (status) => {
  const s = (status || '').toLowerCase();
  return CALL_STATUS_MAP[s] || { label: status || 'Sin estatus', dot: 'bg-zinc-500', text: 'text-zinc-300' };
};

// IVR-UI-001 — Label unificado por tipo/origen de llamada (lee voice_calls).
const getCallTypeLabel = (call) => {
  const callType = call.call_type;
  const triggerSource = call.trigger_source;
  // const initiatedBy = call.initiated_by; // reservado por si se quiere refinar a futuro

  // AI Bridge (con o sin trigger_source)
  if (callType === 'ai_bridge') {
    return 'AI Bridge';
  }

  // IVR moderno (post-desacople), distinguir por trigger_source
  if (callType === 'ivr_nurturing') {
    if (triggerSource === 'automatica') return 'IVR Automático';
    if (triggerSource === 'manual') return 'IVR Manual';
    // Datos antiguos sin trigger_source
    return 'IVR';
  }

  // Legacy: call_type = "nurturing" sin más contexto
  if (callType === 'nurturing') {
    return 'Llamada (legacy)';
  }

  // Bridge manual viejo
  if (callType === 'bridge') {
    return 'Bridge Manual';
  }

  return callType || 'Llamada';
};

// WS-2.1 — Badge por nivel de interés (call_analysis.interest_level).
const INTEREST_BADGE = {
  'Muy Interesado':   { cls: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' },
  'Interesado':       { cls: 'bg-sky-500/15 text-sky-300 border-sky-500/30' },
  'Poco Interesado':  { cls: 'bg-amber-500/15 text-amber-300 border-amber-500/30' },
  'Nada Interesado':  { cls: 'bg-orange-500/15 text-orange-300 border-orange-500/30' },
  'Cero Interactivo': { cls: 'bg-zinc-500/15 text-zinc-300 border-zinc-500/30' },
};

const scoreColorClass = (score) => {
  const n = Number(score) || 0;
  if (n >= 8) return 'text-emerald-400';
  if (n >= 6) return 'text-sky-400';
  if (n >= 4) return 'text-amber-400';
  return 'text-rose-400';
};

// WS-2.1 — Sub-componente con estado colapsable propio para transcript + análisis.
function VoiceCallRow({ call, idx }) {
  const [showTranscript, setShowTranscript] = useState(false);
  const [showAnalysis, setShowAnalysis] = useState(false);

  const statusCfg = getCallStatusCfg(call.status);
  const origin = call.initiated_by ? ORIGIN_BADGE[call.initiated_by] : null;
  const typeLabel = getCallTypeLabel(call);
  const callDate = call.created_at || call.initiated_at || call.ended_at;
  const dur = call.duration_seconds || call.timing?.duration_seconds || 0;
  const sidLead = call.call_sid_lead || call.twilio?.call_sid_lead;
  const sidPromoter = call.call_sid_promoter || call.twilio?.call_sid_promoter;
  const twilioSid = sidLead || sidPromoter;

  const transcriptText = call.transcript_text;
  const hasAnalysis = call.call_analysis && (call.analysis_status === 'completed' || call.call_analysis.analysis_status === 'completed');
  const analysis = call.call_analysis || {};
  const interestCfg = analysis.interest_level ? (INTEREST_BADGE[analysis.interest_level] || { cls: 'bg-zinc-500/15 text-zinc-300 border-zinc-500/30' }) : null;

  return (
    <div className="bg-zinc-900/60 rounded-lg border border-zinc-700 p-4" data-testid={`lead-voice-row-${idx}`}>
      <div className="flex items-center justify-between mb-2 gap-3 flex-wrap">
        <span
          className="inline-flex items-center px-2 py-0.5 rounded-md border border-blue-500/30 bg-blue-500/10 text-blue-200 text-[11px] font-semibold uppercase tracking-wide"
          data-testid={`lead-voice-type-label-${idx}`}
        >
          {typeLabel}
        </span>
        <span className="text-gray-400 text-xs" data-testid={`lead-voice-date-${idx}`}>
          {formatCallDate(callDate)}
        </span>
      </div>
      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          {origin && (
            <span
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-semibold ${origin.cls}`}
              data-testid={`lead-voice-origin-${idx}`}
            >
              <span>{origin.icon}</span>
              <span>{origin.label}</span>
            </span>
          )}
          <div className={`w-2 h-2 rounded-full ${statusCfg.dot}`} data-testid={`lead-voice-status-dot-${idx}`} />
          <span className={`font-medium text-sm ${statusCfg.text}`} data-testid={`lead-voice-status-label-${idx}`}>
            {statusCfg.label}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 mb-3 text-sm" data-testid={`lead-voice-meta-${idx}`}>
        <div>
          <span className="text-gray-400">Duración:</span>
          <span className="text-white ml-2">{dur ? `${dur}s` : '—'}</span>
        </div>
        <div>
          <span className="text-gray-400">Vendedor:</span>
          <span className="text-white ml-2">{call.vendor_phone || 'N/D'}</span>
        </div>
      </div>

      {call.recording_url && (
        <div
          className="bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-blue-500/30 rounded-lg p-3 mb-2"
          data-testid={`lead-voice-recording-wrap-${idx}`}
        >
          <div className="flex items-center gap-2 mb-2">
            <Mic className="w-4 h-4 text-blue-400" />
            <span className="text-white text-xs font-medium">Grabación</span>
          </div>
          <audio
            controls
            preload="metadata"
            className="w-full"
            style={{ height: '36px', borderRadius: '8px' }}
            data-testid={`lead-voice-audio-${idx}`}
          >
            <source src={call.recording_url} type="audio/mpeg" />
            Tu navegador no soporta el reproductor de audio.
          </audio>
        </div>
      )}

      {/* WS-2.1 — Transcripción colapsable */}
      {transcriptText && (
        <div className="mb-2" data-testid={`lead-voice-transcript-wrap-${idx}`}>
          <button
            type="button"
            onClick={() => setShowTranscript(v => !v)}
            className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-md bg-zinc-800/60 hover:bg-zinc-800 border border-white/5 text-left transition-colors"
            data-testid={`lead-voice-transcript-toggle-${idx}`}
          >
            <span className="flex items-center gap-2 text-xs text-white">
              <FileText className="w-3.5 h-3.5 text-cyan-400" />
              <span className="font-medium">Transcripción</span>
              {call.transcript_language && (
                <span className="text-[10px] text-muted-foreground uppercase">{call.transcript_language}</span>
              )}
            </span>
            {showTranscript
              ? <ChevronUp className="w-4 h-4 text-cyan-400" />
              : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
          </button>
          {showTranscript && (
            <div
              className="mt-2 px-3 py-2 rounded-md bg-black/30 border border-white/5 text-xs text-white/80 leading-relaxed whitespace-pre-wrap overflow-y-auto"
              style={{ maxHeight: '200px' }}
              data-testid={`lead-voice-transcript-text-${idx}`}
            >
              {transcriptText}
            </div>
          )}
        </div>
      )}

      {/* WS-2.1 — Análisis IA colapsable */}
      {hasAnalysis && (
        <div className="mb-2" data-testid={`lead-voice-analysis-wrap-${idx}`}>
          <button
            type="button"
            onClick={() => setShowAnalysis(v => !v)}
            className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-md bg-purple-500/5 hover:bg-purple-500/10 border border-purple-500/20 text-left transition-colors"
            data-testid={`lead-voice-analysis-toggle-${idx}`}
          >
            <span className="flex items-center gap-2 text-xs text-white">
              <Sparkles className="w-3.5 h-3.5 text-purple-300" />
              <span className="font-medium">Análisis IA</span>
              {analysis.interest_level && (
                <span
                  className={`inline-flex items-center px-1.5 py-0 rounded border text-[10px] font-medium ${interestCfg.cls}`}
                  data-testid={`lead-voice-analysis-interest-${idx}`}
                >
                  {analysis.interest_level}
                </span>
              )}
              {analysis.sales_skill_score != null && (
                <span className={`text-[10px] font-bold ${scoreColorClass(analysis.sales_skill_score)}`}>
                  {analysis.sales_skill_score}/10
                </span>
              )}
            </span>
            {showAnalysis
              ? <ChevronUp className="w-4 h-4 text-purple-300" />
              : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
          </button>
          {showAnalysis && (
            <div
              className="mt-2 px-3 py-2 rounded-md bg-black/30 border border-purple-500/15 text-xs text-white/80 space-y-2"
              data-testid={`lead-voice-analysis-body-${idx}`}
            >
              {analysis.next_step && (
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-purple-300/80 mb-0.5">Próximo paso</p>
                  <p>{analysis.next_step}</p>
                </div>
              )}
              {Array.isArray(analysis.improvement_suggestions) && analysis.improvement_suggestions.length > 0 && (
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-purple-300/80 mb-0.5">Sugerencias de mejora</p>
                  <ul className="list-disc list-inside space-y-0.5">
                    {analysis.improvement_suggestions.map((s, i) => (
                      <li key={i}>{s}</li>
                    ))}
                  </ul>
                </div>
              )}
              {analysis.summary && (
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-purple-300/80 mb-0.5">Resumen</p>
                  <pre className="whitespace-pre-wrap font-sans text-xs text-white/70">{analysis.summary}</pre>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {twilioSid && (
        <a
          href={twilioCallLink(twilioSid)}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-xs text-blue-300 hover:text-blue-200 underline"
          data-testid={`lead-voice-twilio-link-${idx}`}
        >
          Ver en Twilio
        </a>
      )}
    </div>
  );
}

function AssignmentTraceSection({ leadId, team }) {
  const [trace, setTrace] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!leadId) return;
    setLoading(true);
    api.get(`/leads/${leadId}/assignment-trace`).then(r => setTrace(r.data)).catch(() => setTrace(null)).finally(() => setLoading(false));
  }, [leadId]);

  if (loading) return <div className="text-[10px] text-muted-foreground py-2">Cargando traza...</div>;
  if (!trace?.trace || !trace.trace.level) return null;

  const t = trace.trace;
  const vendorName = trace.vendor_name || 'Sin asignar';
  const groupName = trace.group_name || '';

  const levelConfig = {
    campaign: { icon: '✓', color: 'text-emerald-400', label: 'Campaña' },
    product: { icon: '✓', color: 'text-emerald-400', label: 'Producto' },
    brand: { icon: '✓', color: 'text-emerald-400', label: 'Marca' },
    zone: { icon: '✓', color: 'text-emerald-400', label: 'Zona' },
    property_type: { icon: '✓', color: 'text-emerald-400', label: 'Tipo de Propiedad' },
    // R3: el motor v2 y el worker de horario escriben estos niveles. Sin entrada aquí se
    // pintaba el string técnico crudo ("assignment_v2") — nunca debe verse en pantalla.
    assignment_v2: { icon: '✓', color: 'text-emerald-400', label: 'Asignado por su equipo' },
    awaiting_schedule: { icon: '⏳', color: 'text-amber-400', label: 'En espera por horario' },
    awaiting_schedule_timeout: { icon: '⏳', color: 'text-amber-400', label: 'Asignado a un administrador' },
    awaiting_development_timeout: { icon: '⏳', color: 'text-amber-400', label: 'Asignado por tiempo de espera' },
    // R3b: niveles del motor LEGACY y del Agente IA. Se descubrieron BARRIENDO LA BASE de PROD
    // (distinct+conteo sobre assignment_trace.level), no leyendo el código: `campaign_group_v2`
    // ya NO lo escribe nadie (42 leads históricos) y era invisible desde el código.
    campaign_group: { icon: '✓', color: 'text-emerald-400', label: 'Asignado por el equipo de su campaña' },
    campaign_group_v2: { icon: '✓', color: 'text-emerald-400', label: 'Asignado por el equipo de su campaña' },
    guard_shift: { icon: '✓', color: 'text-emerald-400', label: 'Asignado al vendedor de guardia en turno' },
    round_robin: { icon: '↻', color: 'text-sky-400', label: 'Ruleta general' },
    // R3b (parser de correo): lead de un portal cuyo correo NO tiene campaña mapeada →
    // se asigna a un administrador. Sin esta entrada se pintaría el string técnico crudo (P#9).
    email_portal_sin_mapeo: { icon: '✉', color: 'text-amber-400', label: 'Asignado a un administrador (correo de portal sin campaña configurada)' },
    duplicate: { icon: '⟳', color: 'text-amber-400', label: 'Duplicado detectado' },
    manual: { icon: '✎', color: 'text-violet-400', label: 'Asignacion manual' },
    corporate: { icon: '✓', color: 'text-teal-400', label: 'Dominio corporativo' },
    fallback: { icon: '↻', color: 'text-zinc-400', label: 'Ruleta general' },
    unassigned: { icon: '—', color: 'text-red-400', label: 'Sin asignar' },
  };

  // R3 (log honesto): el LETRERO PRINCIPAL se decide por `reason` (más específico) y si no,
  // por `level`. Así deja de mostrar el texto fijo/mentiroso y nunca pinta un string técnico.
  const reasonLabels = {
    released_on_schedule_open: 'Asignado al abrir el horario de su equipo',
    out_of_schedule_assigned: 'Asignado a su equipo (el botón de respetar horario está apagado)',
    all_members_out_of_schedule: 'En espera: su equipo está fuera de horario. La IA lo está atendiendo; se asignará al abrir.',
    admin_fallback_schedule_never_opened: 'Asignado a un administrador (su equipo no abrió a tiempo)',
    awaiting_development: 'En espera: identificando el desarrollo de interés. La IA lo está atendiendo.',
    // R3b: va al letrero PRINCIPAL (no a una nota al pie) — es algo que el cliente debe ATENDER.
    no_candidates: 'Este lead no tiene vendedor asignado: no había ninguno disponible.',
  };

  // R3: el HINT ("¿cómo mejorar?") solo aparece cuando la causa es de verdad accionable, y con
  // el texto/sugerencia correctos para ESA causa. Las sugerencias apuntan a /team-config, NUNCA
  // al módulo Productos (oculto en autos y desarrollos — CLAUDE.md §3 #7b).
  const hintMap = {
    no_group_matched: { text: 'Fue a la ruleta general: no hay un equipo configurado para este tipo de lead.', action: 'Configura los equipos en Team Config → Guardias.' },
    out_of_schedule_manual: { text: 'Su equipo estaba fuera de horario al crearlo. Se asignó por ruleta general.' },
    all_over_capacity: { text: 'Todos los vendedores de su equipo llegaron a su tope de leads.', action: 'Sube el tope por vendedor.' },
    no_active_vendors: { text: 'No hay vendedores activos que reciban leads.', action: 'Activa vendedores en tu equipo.' },
    // R3b: reason del motor legacy / Agente IA (65 leads en PROD). El QUÉ pasó ya lo dice el
    // letrero principal (reasonLabels) → aquí solo la sugerencia, sin repetir el texto.
    no_candidates: { action: 'Activa vendedores en tu equipo.' },
  };

  const cfg = levelConfig[t.level] || { icon: '?', color: 'text-white', label: t.level };
  const mainLabel = reasonLabels[t.reason] || cfg.label;
  const candidateNames = (t.candidates || []).map(cid => team.find(m => m.id === cid)?.name || cid);

  // R3b: R3 dejó de anexar `rule_name` al label (era redundante con la línea "Grupo: X", que el
  // backend resuelve desde group_id). Pero hay niveles con rule_name ÚTIL y SIN grupo — ej.
  // guard_shift: "Guardia activo - lunes 14:00" — que perdían el dato. Se muestra como línea de
  // detalle SOLO si aporta: no hay grupo resuelto y el rule_name no es texto de relleno.
  const BOILERPLATE_RULE = ['round_robin', 'fallback', 'unassigned', 'duplicate', 'manual'];
  const detailLine = (!groupName && t.rule_name && !BOILERPLATE_RULE.includes(t.level)) ? t.rule_name : '';

  // Estados EN ESPERA: aún no hay vendedor (la IA atiende); no mostrar "Seleccionado: Sin asignar".
  const isAwaiting = t.level === 'awaiting_schedule' || (t.level === 'unassigned' && t.reason === 'awaiting_development');
  // Hint solo para causas accionables. Legacy: round_robin viejo sin reason → no_group_matched.
  const hintKey = t.reason || (t.level === 'round_robin' ? 'no_group_matched' : null);
  const hint = hintKey ? hintMap[hintKey] : null;
  const noPhone = trace.assigned_to && trace.vendor_has_phone === false;

  return (
    <Card className="bg-black/20 border-white/5" data-testid="assignment-trace-card">
      <CardContent className="p-4 space-y-2">
        <p className="text-xs font-semibold text-white/80">¿Por que fue asignado este lead?</p>
        <div className="flex items-start gap-3 p-3 rounded-lg bg-white/[0.03] border border-white/5">
          <span className={`text-lg ${cfg.color}`}>{cfg.icon}</span>
          <div className="flex-1 space-y-1">
            <p className="text-xs text-white font-medium">{mainLabel}</p>
            {groupName && <p className="text-[10px] text-muted-foreground">Grupo: {groupName}</p>}
            {detailLine && <p className="text-[10px] text-muted-foreground">{detailLine}</p>}
            {candidateNames.length > 0 && t.level !== 'manual' && t.level !== 'duplicate' && (
              <p className="text-[10px] text-muted-foreground">Candidatos: {candidateNames.join(', ')}</p>
            )}
            {t.level === 'duplicate' && <p className="text-[10px] text-muted-foreground">Lead anterior conserva dueno: {vendorName}</p>}
            {t.level === 'manual' && <p className="text-[10px] text-muted-foreground">Asignado directamente por el creador del lead</p>}
            {isAwaiting && <p className="text-[10px] text-amber-300/70">→ En espera de asignación (la IA está atendiendo al cliente)</p>}
            {!isAwaiting && t.level !== 'duplicate' && t.level !== 'manual' && (
              <p className="text-[10px] text-sky-400">→ Seleccionado: {vendorName}{typeof t.selected_index === 'number' ? ` (indice ${t.selected_index})` : ''}</p>
            )}
            {t.assigned_at && <p className="text-[9px] text-muted-foreground">{new Date(t.assigned_at).toLocaleString('es-MX')}</p>}
          </div>
        </div>
        {noPhone && (
          <div className="p-3 rounded-lg bg-red-500/5 border border-red-500/10" data-testid="assignment-no-phone-warning">
            <p className="text-[9px] text-red-300/80">⚠ Este vendedor no tiene número de teléfono, no recibió el WhatsApp de aviso.</p>
          </div>
        )}
        {hint && (
          <div className="p-3 rounded-lg bg-amber-500/5 border border-amber-500/10 space-y-1.5" data-testid="assignment-improve-hint">
            <p className="text-[10px] font-medium text-amber-400">¿Como mejorar la asignacion?</p>
            {hint.text && <p className="text-[9px] text-amber-300/70">{hint.text}</p>}
            {hint.action && <p className="text-[9px] text-muted-foreground">→ {hint.action}</p>}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function LeadsPage() {
  const { user } = useAuth();
  const listRef = useRef(null);
  const { exportTab, exporting: exportingListPdf } = useChartExport();
  const handleDownloadListPdf = () => {
    exportTab(listRef, 'Leads', user?.company_name || user?.company?.name, user?.name);
  };
  const allowedModules = user?.allowed_modules || user?.company?.allowed_modules || {};
  // tipo-operacion-renta-venta: gate por VERTICAL (user.dashboard_vertical),
  // NUNCA por allowedModules.developments/allowedModules.properties -- esas
  // son toggles de MODULO (configurables aparte, pueden divergir de la
  // vertical real), no señal de vertical. dashboard_vertical ya viene resuelto
  // del backend en el login (_derive_vertical -> _DASHBOARD_KEY_BY_VERTICAL,
  // core/inventory_resolver.py): 'developments'=desarrollos, 'real_estate'=inmobiliaria.
  const showOperationType = user?.dashboard_vertical === 'developments' || user?.dashboard_vertical === 'real_estate';
  const isMobile = useIsMobile();
  // leads-sin-asignar-por-baja-vendedor: mismos roles que el backend
  // (routes/leads.py: list_leads only_unassigned + bulk_reassign_leads),
  // strings reales confirmados en team.py:313 -- NO copiar el gate de solo
  // 'admin' que ya usa el botón de Exportar (ese no incluye manager).
  const canManageOrphans = ['admin', 'manager', 'superadmin'].includes(user?.role);
  const [expandedLeadIds, setExpandedLeadIds] = useState(() => new Set());
  const toggleLeadExpanded = (id) => setExpandedLeadIds(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const [leads, setLeads] = useState([]);
  // paginacion-lista-leads: `total` es el conteo real del backend (todos los
  // leads que matchean los filtros activos, SIN paginar) -- `leads` ahora
  // solo trae la pagina actual. `total` reemplaza a `leads.length` en todo lo
  // que necesita el conteo REAL (contador de cabecera, limite de licencia).
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [cemRatings, setCemRatings] = useState(DEFAULT_CEM_RATINGS);
  const [team, setTeam] = useState([]);
  const [properties, setProperties] = useState([]);
  const [developments, setDevelopments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [leadDetail, setLeadDetail] = useState(null);
  const [dupFamily, setDupFamily] = useState(null);
  const [dupFamilyLeadId, setDupFamilyLeadId] = useState('');
  const [convTabId, setConvTabId] = useState('');
  const [waLogOpen, setWaLogOpen] = useState(false);
  const [debugLeadOpen, setDebugLeadOpen] = useState(false);
  const [debugLeadJson, setDebugLeadJson] = useState(null);
  const [debugLeadLoading, setDebugLeadLoading] = useState(false);
  const [radioOpen, setRadioOpen] = useState(false);
  const [manualIvrLoading, setManualIvrLoading] = useState(false);
  // Registro de actividad comercial.
  const [activityDialogOpen, setActivityDialogOpen] = useState(false);
  // Lead 360, Pieza 2: panel completo de actividades (B2), fijado al lead abierto.
  const [leadActivitiesViewOpen, setLeadActivitiesViewOpen] = useState(false);
  const [leadLimitDialogOpen, setLeadLimitDialogOpen] = useState(false);
  // leads-sin-asignar-por-baja-vendedor: vista de huerfanos + seleccion masiva.
  const [viewOrphans, setViewOrphans] = useState(false);
  const [selectedOrphanIds, setSelectedOrphanIds] = useState(() => new Set());
  const [bulkReassignOpen, setBulkReassignOpen] = useState(false);
  // reasignacion-pareja-masiva (2026-08-26): reemplaza el <Select> de UN
  // vendedor por un multi-select (Set de ids) -- 1 elemento es el caso
  // trivial (todos los leads a ese unico vendedor, backend lo maneja igual
  // que antes, sin rama especial).
  const [bulkReassignVendorIds, setBulkReassignVendorIds] = useState(() => new Set());
  const [bulkReassignLoading, setBulkReassignLoading] = useState(false);
  // archivar-leads-de-prueba (2026-08-26): un solo modal para archivar --
  // sirve tanto para el boton individual de Lead 360 (archiveDialogLeadIds
  // con 1 elemento) como para el masivo desde la vista "Sin asignar"
  // (archiveDialogLeadIds = Array.from(selectedOrphanIds)). Desarchivar NO
  // usa modal (sin motivo obligatorio) -- es un solo click.
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [archiveDialogLeadIds, setArchiveDialogLeadIds] = useState([]);
  const [archiveReason, setArchiveReason] = useState('');
  const [archiveLoading, setArchiveLoading] = useState(false);
  const [unarchiveLoading, setUnarchiveLoading] = useState(false);
  // Reasignacion individual desde Lead 360 (reemplaza el badge Score IA
  // cuando el lead tiene vendor_removal_snapshot).
  const [detailReassignVendor, setDetailReassignVendor] = useState('');
  const [detailReassignLoading, setDetailReassignLoading] = useState(false);
  // Historial de asesores asignados (Lead 360) -- colapsable, informativo.
  const [reassignmentHistoryOpen, setReassignmentHistoryOpen] = useState(false);
  // archivar-leads-de-prueba: historial de archivado/desarchivado -- mismo
  // patron colapsable que el de arriba.
  const [archiveHistoryOpen, setArchiveHistoryOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [leadSources, setLeadSources] = useState([]);
  const [products, setProducts] = useState([]);
  const [brands, setBrands] = useState([]);
  const [filterLeadSource, setFilterLeadSource] = useState('all');
  const [filterProduct, setFilterProduct] = useState('all');
  const [filterBrand, setFilterBrand] = useState('all');
  const [filterMasiva, setFilterMasiva] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [filterCampaign, setFilterCampaign] = useState('all');
  const [filterDatePreset, setFilterDatePreset] = useState('this_month');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [filterVendor, setFilterVendor] = useState('all');
  const [groups, setGroups] = useState([]);
  const [filterGroup, setFilterGroup] = useState('all');
  const [filterSearch, setFilterSearch] = useState('');
  const [facets, setFacets] = useState(null);
  // archivar-leads-de-prueba (2026-08-26): lo archivado se oculta por
  // defecto; esta casilla lo revela.
  // unificar-archivar-eliminar-leads (2026-08-27): el toggle "Ver
  // suspendidos" (Fase 4.5) se RETIRA de esta pantalla -- "Eliminar" ya no
  // existe para leads (ver archive_leads/unarchive_leads), y los leads que
  // ya estaban suspendidos se migraron a archived=true, asi que ningun lead
  // vuelve a tener is_active=false. El componente compartido VerSuspendidos
  // sigue existiendo para Properties/CarDealer/Opportunities/Brands.
  const [verArchivados, setVerArchivados] = useState(false);
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [productMap, setProductMap] = useState({});
  const [sourceMap, setSourceMap] = useState({});
  const [form, setForm] = useState({ name: '', email: '', phone: '', bsuid: '', source: 'manual', campaign_name: '', notes: '', assigned_to: '', properties_of_interest: [], development_id: '', operation_type: '', lead_source_id: '', brand_id: '', product_id: '' });
  const leadLimit = Number(user?.license_profile?.limits?.contacts ?? -1);
  // paginacion-lista-leads: `total` (conteo real del backend), no `leads.length`
  // (solo la pagina actual) -- antes `leads` traia todo lo filtrado (hasta 1000).
  const isLeadLimitReached = Number.isFinite(leadLimit) && leadLimit >= 0 && total >= leadLimit;

  // Guard de secuencia (2026-08-27): loadData se dispara de nuevo en cada
  // toggle de filtro (viewOrphans, verArchivados, etc.) via
  // useCallback+useEffect. Sin esto, si dos toggles seguidos disparan dos
  // requests y la MAS VIEJA resuelve DESPUES de la mas nueva (orden de red no
  // garantizado), su resultado pisaria el de la request correcta con datos
  // obsoletos -- una condicion de carrera clasica, no exclusiva de ningun
  // filtro en particular. Cada llamada se numera; al resolver, si ya no es
  // la ULTIMA emitida, se descarta sin tocar el estado.
  const loadDataSeqRef = useRef(0);

  // unificar-archivar-eliminar-leads (2026-08-27, 2do ajuste): "Ver
  // archivados" fuerza viewOrphans a false para salir de la plantilla de
  // huerfanos (viewOrphans no es solo un parametro de query -- TAMBIEN
  // decide que UI se renderiza, ver el `{viewOrphans ? ... : ...}` de mas
  // abajo). A diferencia de TODOS los demas filtros (que solo se dejan de
  // ENVIAR, sin tocar su estado), forzar viewOrphans a false sin recordar
  // su valor anterior perderia "Sin asignar" para siempre si estaba activo
  // al prender "Ver archivados". Este ref guarda ese valor para
  // restaurarlo al apagar "Ver archivados".
  const prevViewOrphansRef = useRef(false);

  // paginacion-lista-leads: extraido de loadData para reusarlo tal cual en
  // exportLeadsXlsx (mismo query builder, filtros identicos) -- ahi se pide
  // con limit=0 ("traer todo lo filtrado") en vez de paginar.
  const buildLeadsFilterParams = useCallback(() => {
    const params = {};
    if (verArchivados) {
      // unificar-archivar-eliminar-leads (2026-08-27, decision explicita de
      // Mauricio): "Ver archivados" es un MODO DE VISTA EXCLUSIVO -- ignora
      // TODOS los demas filtros (fecha/vendedor/grupo/busqueda/estado/sin
      // asignar/etc.), se manda SOLO este flag. El backend corta temprano
      // en _build_leads_filter_query devolviendo {company_id, archived:true}
      // y nada mas -- ningun otro parametro de aqui tendria efecto igual.
      params.include_archived = true;
    } else {
      if (filterStatus !== 'all') params.status = filterStatus;
      if (filterLeadSource !== 'all') params.lead_source_id = filterLeadSource;
      if (filterProduct !== 'all') params.product_id = filterProduct;
      if (filterBrand !== 'all') params.brand_id = filterBrand;
      if (filterMasiva !== null) params.has_mass_campaign = filterMasiva;
      if (filterCampaign !== 'all') params.campaign_id = filterCampaign;
      if (filterVendor !== 'all') params.assigned_to = filterVendor;
      if (filterGroup !== 'all') params.group_id = filterGroup;
      if (debouncedSearch.trim()) params.search = debouncedSearch.trim();
      // leads-sin-asignar-por-baja-vendedor: esta vista ignora vendedor/grupo
      // (el backend los sobreescribe igual, ver routes/leads.py::list_leads) --
      // se manda solo el flag. Renombrado de unassigned_reason='vendor_removed'
      // (2026-08-25): la vista ya no es solo "baja de vendedor", tambien trae
      // "nunca asignado" -- un string de "reason" ya no describia el criterio.
      if (viewOrphans) params.only_unassigned = true;
      if (filterDatePreset && filterDatePreset !== 'all') {
        params.date_preset = filterDatePreset;
        if (filterDatePreset === 'custom') {
          if (filterDateFrom) params.date_from = filterDateFrom;
          if (filterDateTo) params.date_to = filterDateTo;
        }
      }
    }
    return params;
  }, [verArchivados, filterStatus, filterLeadSource, filterProduct, filterBrand, filterMasiva, filterCampaign, filterVendor, filterGroup, debouncedSearch, viewOrphans, filterDatePreset, filterDateFrom, filterDateTo]);

  const loadData = useCallback(() => {
    const seq = ++loadDataSeqRef.current;
    const params = buildLeadsFilterParams();
    params.skip = (page - 1) * pageSize;
    params.limit = pageSize;

    const calls = [
      api.get('/leads', { params }),
      api.get('/leads/filter-facets', { params }).catch(() => ({ data: null })),
      api.get('/team'),
      api.get('/properties'),
      api.get('/campaigns', { params: { include_hidden: true } }).catch(() => ({ data: [] })),
      api.get('/roulette/groups').catch(() => ({ data: [] })),
    ];
    if (allowedModules.products) {
      calls.push(api.get('/lead-sources').catch(() => ({ data: [] })));
      calls.push(api.get('/products').catch(() => ({ data: [] })));
    }
    if (allowedModules.brands) {
      calls.push(api.get('/brands').catch(() => ({ data: [] })));
    }
    if (allowedModules.developments) {
      calls.push(api.get('/developments').catch(() => ({ data: [] })));
    }

    Promise.all(calls).then((results) => {
      if (seq !== loadDataSeqRef.current) return; // respuesta vieja -- ya hay una request mas nueva en curso/resuelta
      const [l, fac, t, p, c, g] = results;
      // paginacion-lista-leads: GET /leads ahora devuelve {items,total,skip,limit}.
      setLeads(markDuplicatesInList(Array.isArray(l.data?.items) ? l.data.items : []));
      setTotal(l.data?.total ?? 0);
      setFacets(fac?.data || null);
      setTeam(t.data); setProperties(p.data);
      setCampaigns(Array.isArray(c?.data) ? c.data : []);
      setGroups(Array.isArray(g?.data) ? g.data.filter(x => x.is_active !== false) : []);
      let idx = 6;
      if (allowedModules.products) {
        setLeadSources(Array.isArray(results[idx]?.data) ? results[idx].data : []);
        const sMap = {}; (Array.isArray(results[idx]?.data) ? results[idx].data : []).forEach(s => { sMap[s.id] = s.name; }); setSourceMap(sMap);
        idx++;
        setProducts(Array.isArray(results[idx]?.data) ? results[idx].data : []);
        const pMap = {}; (Array.isArray(results[idx]?.data) ? results[idx].data : []).forEach(p => { pMap[p.id] = p.name; }); setProductMap(pMap);
        idx++;
      }
      if (allowedModules.brands) {
        setBrands(Array.isArray(results[idx]?.data) ? results[idx].data : []);
        idx++;
      }
      if (allowedModules.developments) {
        const devData = Array.isArray(results[idx]?.data) ? results[idx].data : [];
        setDevelopments(devData);
        idx++;
      }
      setLoading(false);
    }).catch(() => { if (seq === loadDataSeqRef.current) setLoading(false); });
  }, [buildLeadsFilterParams, page, pageSize]);

  useEffect(() => { loadData(); }, [loadData]);
  // paginacion-lista-leads: cualquier cambio de filtro (o de pageSize) debe
  // volver a la pagina 1 -- si no, un filtro nuevo con menos resultados
  // podria dejar `page` apuntando a una pagina que ya no existe (skip mayor
  // al total, la lista se veria vacia sin explicacion).
  useEffect(() => { setPage(1); }, [buildLeadsFilterParams, pageSize]);
  useEffect(() => {
    api.get('/cem-ratings').then(r => {
      if (Array.isArray(r.data?.ratings) && r.data.ratings.length) setCemRatings(r.data.ratings);
    }).catch(() => {});
  }, []);

  // Debounce del buscador: filterSearch (input, inmediato) -> debouncedSearch (dispara GET /leads).
  // Al vaciar el input, debouncedSearch vuelve a '' y la lista regresa al filtro de fecha activo.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(filterSearch), 350);
    return () => clearTimeout(t);
  }, [filterSearch]);

  const getVendorName = (id) => team.find(m => m.id === id)?.name || 'Sin asignar';  const getPropTitle = (id) => properties.find(p => p.id === id)?.title || '';
  // leads-sin-asignar-por-baja-vendedor: nombre para una entrada de
  // reassignment_history. `storedName` es el nombre congelado al momento
  // (entradas nuevas, escrito por push_reassignment_history); las entradas
  // VIEJAS no lo tienen y caen al fallback contra `team` COMPLETO -- a
  // proposito NO el `team.filter(m => m.is_active !== false)` que usan los
  // dropdowns: un vendedor solo DESACTIVADO sigue en `team` con su nombre
  // disponible (GET /team no filtra por is_active), filtrar aca mostraria
  // "Vendedor no disponible" de mas. Si no hay id (primera asignacion o
  // desasignacion total) es "Sin vendedor" -- distinto de "no disponible"
  // (que implica que existio pero no se encuentra).
  //
  // CORRECCION (2026-08-26): tambien la usa el badge "Reasignado: X -> Y" de
  // la lista (antes usaba getVendorName a secas, que NUNCA miraba el nombre
  // guardado -- fallaba igual que el historial de Lead 360 para vendedores
  // borrados, pero encima con un texto de fallback distinto, "Sin asignar").
  // Mismo criterio en los dos lugares, un solo texto de fallback.
  const resolveHistorialVendorName = (rawId, storedName) => {
    const id = typeof rawId === 'string' ? rawId : (Array.isArray(rawId) && typeof rawId[0] === 'string' ? rawId[0] : '');
    if (!id) return 'Sin vendedor';
    if (storedName) return storedName;
    return team.find(m => m.id === id)?.name || 'Vendedor no disponible';
  };
  const getProductName = (id) => productMap[id] || '';
  const getSourceName = (id) => sourceMap[id] || '';
  const getBrandName = (id) => brands.find(b => b.id === id)?.name || id;
  const id8 = (id) => id ? `#${id.substring(0, 8)}` : '';

  // WS-ACTIVIDADES-2: export Excel admin de leads filtrados.
  const exportLeadsXlsx = async () => {
    // paginacion-lista-leads: el export debe traer TODOS los leads que
    // matchean los filtros activos, no solo la pagina cargada en pantalla --
    // fetch aparte con los mismos filtros (buildLeadsFilterParams) y
    // limit=0 ("traer todo lo filtrado"), sin tocar el estado paginado `leads`.
    let exportLeads;
    try {
      const params = buildLeadsFilterParams();
      params.limit = 0;
      const r = await api.get('/leads', { params });
      exportLeads = Array.isArray(r.data?.items) ? r.data.items : [];
    } catch {
      toast.error('No se pudo obtener la lista completa de leads para exportar.');
      return;
    }
    if (!exportLeads.length) {
      toast.error('No hay leads para exportar.');
      return;
    }
    // WS-ACTIVIDADES-2 fix: enriquecer con último origen de actividad si existe.
    const originByLead = {};
    try {
      const leadIds = exportLeads.map(l => l.id).filter(Boolean).slice(0, 500);
      if (leadIds.length) {
        await Promise.all(leadIds.map(async (lid) => {
          try {
            const r = await api.get(`/activities`, { params: { lead_id: lid, limit: 1 } });
            const last = (r.data?.items || [])[0];
            if (last) originByLead[lid] = last.origen || last.source || '';
          } catch { /* silencioso */ }
        }));
      }
    } catch { /* silencioso */ }

    const rows = exportLeads.map(lead => ({
      ID: lead.id,
      Nombre: lead.name || '',
      Email: lead.email || '',
      Teléfono: lead.phone || '',
      Estatus: lead.status || '',
      Calificación: lead.calificacion ?? lead.qualification ?? lead.ai_score ?? '',
      'Score IA': lead.ai_score ?? '',
      'Fuente Lead': lead.lead_source_id ? getSourceName(lead.lead_source_id) : (lead.campaign_name || ''),
      Producto: lead.product_id ? getProductName(lead.product_id) : '',
      Marca: lead.brand_id ? getBrandName(lead.brand_id) : '',
      Desarrollo: lead.development_id ? getDevTitle(lead.development_id) : '',
      'Vendedor Asignado': getVendorName(lead.assigned_to),
      'Campaña Masiva': lead.mass_campaign_id ? 'Sí' : 'No',
      'Último Origen Actividad': originByLead[lead.id] || '',
      Notas: lead.notes || '',
      'Creado': lead.created_at || '',
      'Actualizado': lead.updated_at || '',
      'Último Contacto': lead.last_contact || '',
      'Source': lead.source || '',
      'AI Block': lead.blocked_by_meta ? 'Bloqueado Meta' : '',
    }));
    const headers = Object.keys(rows[0]);
    const ws = XLSX.utils.json_to_sheet(rows, { header: headers });
    ws['!cols'] = headers.map(() => ({ wch: 22 }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Leads');
    const ts = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(wb, `leads-${ts}.xlsx`);
    toast.success(`Exportados ${rows.length} leads`);
  };

  const openNew = () => {
    if (isLeadLimitReached) {
      setLeadLimitDialogOpen(true);
      return;
    }
    setEditing(null);
    setForm({ name: '', email: '', phone: '', bsuid: '', source: 'manual', campaign_name: '', notes: '', assigned_to: '', properties_of_interest: [], development_id: '', operation_type: '', lead_source_id: '', brand_id: '', product_id: '' });
    setDialogOpen(true);
  };

  const openEdit = (lead) => {
    setEditing(lead.id);
    setForm({
      name: lead.name || '', email: lead.email || '', phone: lead.phone || '', bsuid: lead.bsuid || '',
      source: lead.source || 'manual', campaign_name: lead.campaign_name || '',
      notes: '', assigned_to: lead.assigned_to || '',
      properties_of_interest: lead.properties_of_interest || [],
      development_id: lead.development_id || '',
      operation_type: lead.operation_type || '',
    });
    setDialogOpen(true);
  };

  // Un contacto de WhatsApp que escondio su numero llega con un identificador
  // de Meta (BSUID) en vez de telefono: dos letras, punto y alfanumericos.
  // Ej: MX.13491208655302741918. Al vendedor no se le ensena eso en crudo.
  const esBSUID = (v) => /^[A-Z]{2}\.[A-Za-z0-9]{6,}$/.test(String(v || '').trim());
  const comoTelefono = (v) => (esBSUID(v) ? 'WhatsApp sin número' : v);

  const saveLead = async () => {
    if (!String(form.name || '').trim()) {
      toast.error('Nombre es obligatorio');
      return;
    }
    if (!String(form.phone || '').trim() && !String(form.email || '').trim() && !String(form.bsuid || '').trim()) {
      toast.error('Indica al menos un medio de contacto: teléfono, correo o BSUID');
      return;
    }
    if (!String(form.source || '').trim()) {
      toast.error('Canal/Origen es obligatorio');
      return;
    }
    // Solo vertical desarrollos (MISMO gate que el selector: allowedModules.developments) y SOLO al CREAR:
    // el desarrollo es obligatorio. Atiende WABA compartido (1 línea, N desarrollos) donde el desarrollo
    // del lead se captura a mano. En otras verticales el selector ni aparece → este check no aplica.
    if (!editing && allowedModules.developments && !String(form.development_id || '').trim()) {
      toast.error('Selecciona un desarrollo');
      return;
    }

    try {
      const payload = { ...form };
      if (!payload.assigned_to) delete payload.assigned_to;
      if (editing) {
        await api.put(`/leads/${editing}`, payload);
        toast.success('Lead actualizado');
      } else {
        await api.post('/leads', payload);
        toast.success('Lead creado');
      }
      setDialogOpen(false);
      loadData();
    } catch (e) {
      const detail = e.response?.data?.detail || 'Error';
      if (e.response?.status === 403 && String(detail).toLowerCase().includes('limite')) {
        setLeadLimitDialogOpen(true);
      }
      toast.error(detail);
    }
  };

  const updateStatus = async (lead, newStatus) => {
    await api.put(`/leads/${lead.id}`, { status: newStatus });
    toast.success(`Calificación: ${ratingLabel(newStatus, cemRatings)}`);
    loadData();
  };

  // leads-sin-asignar-por-baja-vendedor: "Seleccionar todos" en la vista de
  // huerfanos. Tilda como maximo BULK_REASSIGN_MAX (el mismo tope que ya
  // valida bulk_reassign_leads en el backend) -- si tildara mas, el 400 le
  // llegaria al usuario RECIEN despues de elegir vendedor destino y darle a
  // "Reasignar", cuando ya invirtio el tiempo de elegir.
  const toggleSelectAllOrphans = () => {
    // archivar-leads-de-prueba (2026-08-26): un lead archivado NO es
    // seleccionable para reasignar (con "Ver archivados" activo, esta misma
    // lista los mezcla con los activos) -- "todos" significa "todos los
    // seleccionables", igual que el backend los excluye de bulk-reassign.
    const seleccionables = leads.filter(l => !l.archived);
    const allSelected = seleccionables.length > 0 && seleccionables.every(l => selectedOrphanIds.has(l.id));
    if (allSelected) {
      setSelectedOrphanIds(new Set());
      return;
    }
    const capped = seleccionables.slice(0, BULK_REASSIGN_MAX).map(l => l.id);
    setSelectedOrphanIds(new Set(capped));
    if (seleccionables.length > BULK_REASSIGN_MAX) {
      toast.info(`Se seleccionaron los primeros ${BULK_REASSIGN_MAX} de ${seleccionables.length} — repetí la operación para el resto.`);
    }
  };

  // reasignacion-pareja-masiva (2026-08-26): preview client-side del reparto,
  // MISMO algoritmo que el backend (round-robin por indice, vendor_ids[i % M])
  // -- para que el usuario vea el resultado ANTES de confirmar, sin llamada
  // extra (es determinista: mismo orden de leads/vendedores, mismo resultado).
  const _previewBulkReassignDistribution = () => {
    const vendorIds = Array.from(bulkReassignVendorIds);
    const m = vendorIds.length;
    if (m === 0) return [];
    const conteo = new Map(vendorIds.map(id => [id, 0]));
    const n = selectedOrphanIds.size;
    for (let i = 0; i < n; i++) {
      const vid = vendorIds[i % m];
      conteo.set(vid, conteo.get(vid) + 1);
    }
    return vendorIds.map(id => ({
      vendor_id: id,
      vendor_name: team.find(t => t.id === id)?.name || id,
      count: conteo.get(id),
    }));
  };

  // leads-sin-asignar-por-baja-vendedor: reasignacion masiva desde la vista
  // de huerfanos -- una sola llamada server-side (POST /leads/bulk-reassign),
  // no N PUT /leads/{id} desde el frontend. reasignacion-pareja-masiva
  // (2026-08-26): con 1 solo vendedor elegido se manda `assigned_to` (NO
  // `vendor_ids`) -- decision de Mauricio: elegir 1 "funciona igual, le
  // tocan todos" incluye que el historial quede IGUAL que siempre
  // (source="bulk_reassign", "Reasignación masiva"), no el source nuevo. El
  // source "bulk_reassign_split" queda reservado EXCLUSIVAMENTE para 2+
  // vendedores (reparto real). El backend ya soporta ambos modos sin cambios.
  const submitBulkReassign = async () => {
    if (bulkReassignVendorIds.size === 0 || selectedOrphanIds.size === 0) return;
    setBulkReassignLoading(true);
    try {
      const vendorIdsArr = Array.from(bulkReassignVendorIds);
      const body = vendorIdsArr.length === 1
        ? { lead_ids: Array.from(selectedOrphanIds), assigned_to: vendorIdsArr[0] }
        : { lead_ids: Array.from(selectedOrphanIds), vendor_ids: vendorIdsArr };
      const { data } = await api.post('/leads/bulk-reassign', body);
      const distribution = data?.distribution;
      if (Array.isArray(distribution) && distribution.length > 1) {
        toast.success(`Leads repartidos: ${distribution.map(d => `${d.vendor_name || d.vendor_id} (${d.count})`).join(' · ')}`);
      } else {
        toast.success('Leads reasignados');
      }
      setBulkReassignOpen(false);
      setSelectedOrphanIds(new Set());
      setBulkReassignVendorIds(new Set());
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al reasignar leads');
    } finally {
      setBulkReassignLoading(false);
    }
  };

  // archivar-leads-de-prueba (2026-08-26): abre el modal de motivo. Sirve
  // para 1 lead (Lead 360) o para el bloque seleccionado (checkbox de "Sin
  // asignar") -- el mismo endpoint recibe `lead_ids` en ambos casos.
  const openArchiveDialog = (leadIds) => {
    setArchiveDialogLeadIds(leadIds);
    setArchiveReason('');
    setArchiveDialogOpen(true);
  };

  const submitArchive = async () => {
    if (archiveDialogLeadIds.length === 0 || !archiveReason.trim()) return;
    setArchiveLoading(true);
    try {
      await api.post('/leads/archive', { lead_ids: archiveDialogLeadIds, reason: archiveReason.trim() });
      toast.success(archiveDialogLeadIds.length > 1 ? 'Leads archivados' : 'Lead archivado');
      setArchiveDialogOpen(false);
      setArchiveDialogLeadIds([]);
      setArchiveReason('');
      setSelectedOrphanIds(new Set());
      if (leadDetail?.lead?.id && archiveDialogLeadIds.includes(leadDetail.lead.id)) {
        await fetchLeadDetails(leadDetail.lead.id);
      }
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al archivar leads');
    } finally {
      setArchiveLoading(false);
    }
  };

  // Desarchivar: sin motivo obligatorio (confirmado por Mauricio) -- un
  // solo click, sin modal.
  const submitUnarchive = async (leadId) => {
    if (!leadId) return;
    setUnarchiveLoading(true);
    try {
      await api.post('/leads/unarchive', { lead_ids: [leadId] });
      toast.success('Lead desarchivado');
      if (leadDetail?.lead?.id === leadId) {
        await fetchLeadDetails(leadId);
      }
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al desarchivar el lead');
    } finally {
      setUnarchiveLoading(false);
    }
  };

  // Reasignacion individual desde Lead 360 -- mismo endpoint que ya usa
  // updateStatus (PUT /leads/{id}), solo que aqui cambia assigned_to.
  const submitDetailReassign = async () => {
    const leadId = leadDetail?.lead?.id;
    if (!leadId || !detailReassignVendor) return;
    setDetailReassignLoading(true);
    try {
      await api.put(`/leads/${leadId}`, { assigned_to: detailReassignVendor });
      toast.success('Lead reasignado');
      setDetailReassignVendor('');
      await fetchLeadDetails(leadId);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al reasignar el lead');
    } finally {
      setDetailReassignLoading(false);
    }
  };

  const toggleProperty = (propId) => {
    const current = form.properties_of_interest || [];
    const updated = current.includes(propId) ? current.filter(p => p !== propId) : [...current, propId];
    setForm({ ...form, properties_of_interest: updated });
  };

  const getDevTitle = (id) => developments.find(d => d.id === id)?.name || developments.find(d => d.id === id)?.title || '';

  // Selección ÚNICA: click en el desarrollo seleccionado lo deselecciona; click en otro lo reemplaza.
  const toggleDevelopment = (devId) => {
    setForm({ ...form, development_id: form.development_id === devId ? '' : devId });
  };

  const fetchLeadDetails = async (leadId) => {
    const r = await api.get(`/leads/${leadId}/detail`);
    setLeadDetail(r.data);
    setConvTabId(leadId);
    return r.data;
  };

  const openDetail = async (leadId) => {
    try {
      await fetchLeadDetails(leadId);
      setDetailOpen(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al cargar detalle del lead');
    }
  };

  // Auto-abrir el DETALLE de un lead al llegar con ?open=<leadId> (p.ej. "Ver lead" desde la Agenda).
  // Antes ese botón usaba ?highlight= y solo caía en el panel; ahora abre la ficha 360 directo.
  useEffect(() => {
    let leadId = '';
    try { leadId = new URLSearchParams(window.location.search).get('open') || ''; } catch { leadId = ''; }
    if (!leadId) return;
    openDetail(leadId);
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete('open');
      window.history.replaceState({}, '', url.pathname + url.search);
    } catch { /* noop */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openDupFamily = async (e, leadId) => {
    e.stopPropagation();
    try {
      const r = await api.get(`/leads/${leadId}/duplicate-family`);
      setDupFamily(r.data);
      setDupFamilyLeadId(leadId);
    } catch {
      toast.error('No se pudieron cargar las consultas previas');
    }
  };

  const manualIVRCall = async (leadId) => {
    if (!leadId) return;
    setManualIvrLoading(true);
    try {
      const response = await api.post('/ivr/manual-call', { lead_id: leadId });
      if (response.data?.success) {
        toast.success('Llamada IVR iniciada correctamente');
      } else {
        toast.error(response.data?.message || 'Error al iniciar llamada IVR');
      }
      await fetchLeadDetails(leadId);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al iniciar llamada IVR');
    } finally {
      setManualIvrLoading(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div data-testid="leads-page">
      <div className="flex items-center justify-between mb-6 sm:mb-8 flex-wrap gap-3">
        <div>
          <h2 className="font-heading text-xl sm:text-2xl font-bold text-white">Leads</h2>
          <p className="text-muted-foreground text-sm mt-1">{total} leads registrados</p>
          {isLeadLimitReached && <p className="text-[11px] text-red-300 mt-1" data-testid="lead-limit-warning">Límite de leads alcanzado para tu licencia.</p>}
        </div>
        <div className="flex flex-wrap gap-3">
          {user?.role === 'admin' && (
            <Button variant="outline" onClick={exportLeadsXlsx} className="border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10" data-testid="export-leads-xlsx-btn" title="Exportar Excel (admin)">
              <FileSpreadsheet className="w-4 h-4 mr-2" />Exportar Excel
            </Button>
          )}
          <Button variant="outline" onClick={handleDownloadListPdf} disabled={exportingListPdf} className="border-cyan-500/30 text-cyan-300 hover:bg-cyan-500/10" data-testid="download-leads-pdf-btn" title="Descargar el listado visible como PDF">
            <Download className="w-4 h-4 mr-2" />{exportingListPdf ? 'Generando…' : 'Descargar Listado PDF'}
          </Button>
          {canManageOrphans && (
            <Button
              variant="outline"
              disabled={verArchivados}
              onClick={() => { setViewOrphans(v => !v); setSelectedOrphanIds(new Set()); }}
              className={`border-amber-500/30 text-amber-300 hover:bg-amber-500/10 disabled:opacity-40 disabled:cursor-not-allowed ${viewOrphans ? 'bg-amber-500/10' : ''}`}
              data-testid="toggle-orphans-view-btn"
              title={verArchivados ? 'No disponible mientras "Ver archivados" está activo' : undefined}
            >
              <Users className="w-4 h-4 mr-2" />
              {viewOrphans ? 'Ver todos los leads' : 'Sin asignar'}
            </Button>
          )}
          <Button onClick={openNew} className={`font-semibold ${isLeadLimitReached ? 'bg-red-600/20 border border-red-500/50 text-red-200 hover:bg-red-600/30' : 'bg-primary hover:bg-primary/90 shadow-lg shadow-primary/20'}`} data-testid="add-lead-btn">
            <Plus className="w-4 h-4 mr-2" />Nuevo Lead
          </Button>
        </div>
      </div>
      {/* Opción C: 3 filas. Fila 1 = Buscar+Status+Campaña, Fila 2 = Vendedor+Grupo+Rango,
          Fila 3 = "+ Más filtros" (Fuente/Producto/Marca/Masiva) + Ver archivados. */}
      <div className="flex gap-3 mb-3 flex-wrap items-end">
        <FilterField label="Buscar" width="200px">
          <input
            type="text"
            value={filterSearch}
            onChange={e => setFilterSearch(e.target.value)}
            placeholder="Nombre, teléfono o email"
            disabled={verArchivados}
            className="w-full h-full text-xs border border-white/10 rounded-md px-3 py-1 bg-background text-foreground disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="filter-search"
          />
        </FilterField>

        <FilterField label="Status">
          <Select value={filterStatus} onValueChange={setFilterStatus} disabled={verArchivados}>
            <SelectTrigger className="w-full h-full bg-black/20 border-white/10" data-testid="filter-status"><SelectValue placeholder="Estado" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas las calificaciones</SelectItem>
              {cemRatings.map(r => (
                <SelectItem key={r.id} value={r.id}>{r.label}</SelectItem>
              ))}
              {(facets?.status || []).filter(s => !cemRatings.some(r => r.id === s)).map(s => (
                <SelectItem key={s} value={s}>{ratingLabel(s, cemRatings)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FilterField>

        {campaigns.length > 0 && (
          <FilterField label="Campaña">
            <Select
              value={filterCampaign}
              disabled={verArchivados}
              onValueChange={(v) => {
                setFilterCampaign(v);
                if (v !== 'all' && filterGroup !== 'all') {
                  const camp = campaigns.find(c => c.id === v);
                  if (camp?.group_id && camp.group_id !== filterGroup) setFilterGroup(camp.group_id);
                }
              }}
            >
              <SelectTrigger className="w-full h-full bg-black/20 border-white/10" data-testid="filter-campaign"><SelectValue placeholder="Campaña" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{filterGroup !== 'all' ? 'Todas las del grupo' : 'Todas las campañas'}</SelectItem>
                {byFacet(campaignsVisibleForGroup(campaigns, filterGroup), facets?.campaign_id, filterCampaign).map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </FilterField>
        )}
      </div>

      <div className="flex gap-3 mb-3 flex-wrap items-end">
        {user?.role !== 'vendor' && (
          <FilterField label="Vendedor">
            <Select
              value={filterVendor}
              disabled={verArchivados}
              onValueChange={(v) => {
                setFilterVendor(v);
                if (v !== 'all' && filterGroup !== 'all') {
                  const g = groups.find(x => x.id === filterGroup);
                  if (!memberIdsOfGroup(g).includes(v)) setFilterGroup('all');
                }
              }}
            >
              <SelectTrigger className="w-full h-full bg-black/20 border-white/10" data-testid="filter-vendor"><SelectValue placeholder="Vendedor" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{filterGroup !== 'all' ? 'Todos los del grupo' : 'Todos los vendedores'}</SelectItem>
                {byFacet(vendorsVisibleForGroup(team, groups, filterGroup), facets?.assigned_to, filterVendor).map(m => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </FilterField>
        )}

        {user?.role !== 'vendor' && groups.length > 0 && (
          <FilterField label="Grupo">
            <Select
              value={filterGroup}
              disabled={verArchivados}
              onValueChange={(v) => {
                setFilterGroup(v);
                if (v !== 'all' && filterVendor !== 'all') {
                  const g = groups.find(x => x.id === v);
                  if (!memberIdsOfGroup(g).includes(filterVendor)) setFilterVendor('all');
                }
                if (filterCampaign !== 'all') {
                  const camp = campaigns.find(c => c.id === filterCampaign);
                  if (camp?.group_id && camp.group_id !== v) setFilterCampaign('all');
                }
              }}
            >
              <SelectTrigger className="w-full h-full bg-black/20 border-white/10" data-testid="filter-group"><SelectValue placeholder="Grupo" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{filterVendor !== 'all' ? 'Todos los grupos de este vendedor' : 'Todos los grupos'}</SelectItem>
                {byFacet(groupsVisibleForCampaign(groupsVisibleForVendor(groups, filterVendor), campaigns, filterCampaign), facets?.group_id, filterGroup).map(gr => <SelectItem key={gr.id} value={gr.id}>{gr.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </FilterField>
        )}

        <FilterField label="Rango" width="240px">
          <Select value={filterDatePreset} onValueChange={setFilterDatePreset} disabled={verArchivados}>
            <SelectTrigger className="w-full h-full bg-black/20 border-white/10" data-testid="filter-date-preset"><SelectValue placeholder="Rango de fecha" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="today">Hoy</SelectItem>
              <SelectItem value="yesterday">Ayer</SelectItem>
              <SelectItem value="last_7_days">Últimos 7 días</SelectItem>
              <SelectItem value="this_month">Este mes</SelectItem>
              <SelectItem value="last_month">Mes pasado</SelectItem>
              <SelectItem value="custom">Personalizado</SelectItem>
            </SelectContent>
          </Select>
        </FilterField>
        {filterDatePreset === 'custom' && (
          <>
            <FilterField label="Desde" width="110px">
              <input type="date" value={filterDateFrom} onChange={e => setFilterDateFrom(e.target.value)} disabled={verArchivados} className="w-full h-full text-xs border border-white/10 rounded-md px-2 py-1 bg-background text-foreground disabled:opacity-40 disabled:cursor-not-allowed" data-testid="filter-date-from" />
            </FilterField>
            <FilterField label="Hasta" width="110px">
              <input type="date" value={filterDateTo} onChange={e => setFilterDateTo(e.target.value)} disabled={verArchivados} className="w-full h-full text-xs border border-white/10 rounded-md px-2 py-1 bg-background text-foreground disabled:opacity-40 disabled:cursor-not-allowed" data-testid="filter-date-to" />
            </FilterField>
          </>
        )}
      </div>

      <div className="flex gap-3 mb-6 flex-wrap items-end">
        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              disabled={verArchivados}
              className="h-8 px-3 text-xs flex items-center gap-2 border border-white/10 rounded-md bg-black/20 text-foreground disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="filter-more-toggle"
            >
              + Más filtros
              <ChevronDown className="w-3.5 h-3.5" />
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-56" align="start">
            <div className="flex flex-col gap-4">
              {allowedModules.products && leadSources.length > 0 && (
                <FilterField label="Fuente">
                  <Select value={filterLeadSource} onValueChange={setFilterLeadSource} disabled={verArchivados}>
                    <SelectTrigger className="w-full h-full bg-black/20 border-white/10" data-testid="filter-lead-source"><SelectValue placeholder="Fuente de Lead" /></SelectTrigger>
                    <SelectContent><SelectItem value="all">Todas las Fuentes</SelectItem>{byFacet(leadSources, facets?.lead_source_id, filterLeadSource).map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                  </Select>
                </FilterField>
              )}

              {allowedModules.products && products.length > 0 && (
                <FilterField label="Producto">
                  <Select value={filterProduct} onValueChange={setFilterProduct} disabled={verArchivados}>
                    <SelectTrigger className="w-full h-full bg-black/20 border-white/10" data-testid="filter-product"><SelectValue placeholder="Producto" /></SelectTrigger>
                    <SelectContent><SelectItem value="all">Todos los Productos</SelectItem>{byFacet(products, facets?.product_id, filterProduct).map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                  </Select>
                </FilterField>
              )}

              {allowedModules.brands && brands.length > 0 && (
                <FilterField label="Marca">
                  <Select value={filterBrand} onValueChange={setFilterBrand} disabled={verArchivados}>
                    <SelectTrigger className="w-full h-full bg-black/20 border-white/10" data-testid="filter-brand"><SelectValue placeholder="Marca" /></SelectTrigger>
                    <SelectContent><SelectItem value="all">Todas las Marcas</SelectItem>{byFacet(brands, facets?.brand_id, filterBrand).map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
                  </Select>
                </FilterField>
              )}

              <FilterField label="Campaña Masiva">
                <select
                  value={filterMasiva === null ? '' : String(filterMasiva)}
                  onChange={e => {
                    const v = e.target.value;
                    setFilterMasiva(v === '' ? null : v === 'true');
                  }}
                  disabled={verArchivados}
                  className="w-full h-full text-xs border border-white/10 rounded-md px-2 py-1 bg-background text-foreground disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="filter-masiva"
                >
                  <option value="">Todas las fuentes</option>
                  <option value="true">📣 Respondió a masiva</option>
                  <option value="false">Sin atribución masiva</option>
                </select>
              </FilterField>
            </div>
          </PopoverContent>
        </Popover>

        <div className="self-end pb-1">
          <VerSuspendidos
            checked={verArchivados}
            onChange={(v) => {
              setVerArchivados(v);
              // unificar-archivar-eliminar-leads (2026-08-27): "Ver archivados"
              // y "Sin asignar" son dos MODOS DE VISTA mutuamente excluyentes,
              // no dos checkboxes independientes -- activar uno apaga el otro.
              // Al activar, se GUARDA el viewOrphans previo (prevViewOrphansRef)
              // antes de forzarlo a false; al desactivar, se RESTAURA -- si no,
              // "Sin asignar" se perderia para siempre al pasar por "Ver
              // archivados" (unico filtro que rompia esa garantia).
              if (v) {
                prevViewOrphansRef.current = viewOrphans;
                setViewOrphans(false);
                setSelectedOrphanIds(new Set());
              } else {
                setViewOrphans(prevViewOrphansRef.current);
              }
            }}
            testid="leads-ver-archivados"
            label="Ver archivados"
            title="Leads archivados por ser de prueba u otro motivo -- reversible, no se borro nada. Vista exclusiva: ignora los demas filtros."
          />
        </div>
      </div>
      {viewOrphans ? (
        <div className="space-y-3" data-testid="orphans-view">
          {leads.length > 0 && (() => {
            // archivar-leads-de-prueba: "todos" = todos los SELECCIONABLES
            // (no archivados) -- mismo criterio que toggleSelectAllOrphans.
            const seleccionables = leads.filter(l => !l.archived);
            return (
              <div className="flex items-center gap-2" data-testid="select-all-orphans-row">
                <input
                  type="checkbox"
                  checked={seleccionables.length > 0 && seleccionables.every(l => selectedOrphanIds.has(l.id))}
                  onChange={toggleSelectAllOrphans}
                  className="w-4 h-4 shrink-0"
                  data-testid="select-all-orphans-checkbox"
                />
                <label
                  className="text-xs text-muted-foreground cursor-pointer select-none"
                  onClick={toggleSelectAllOrphans}
                >
                  Seleccionar todos {seleccionables.length > BULK_REASSIGN_MAX ? `(máx. ${BULK_REASSIGN_MAX} de ${seleccionables.length})` : `(${seleccionables.length})`}
                </label>
              </div>
            );
          })()}
          {selectedOrphanIds.size > 0 && (
            <div className="flex items-center justify-between gap-3 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20" data-testid="bulk-reassign-bar">
              <p className="text-xs text-white">{selectedOrphanIds.size} lead(s) seleccionado(s)</p>
              <div className="flex items-center gap-2">
                <Button size="sm" onClick={() => setBulkReassignOpen(true)} data-testid="bulk-reassign-open-btn">
                  Reasignar seleccionados
                </Button>
                {/* archivar-leads-de-prueba (2026-08-26): mismo bloque seleccionado,
                    accion distinta -- un solo motivo aplica a todos. */}
                <Button
                  size="sm"
                  variant="outline"
                  className="border-white/10 text-zinc-300 hover:text-white"
                  onClick={() => openArchiveDialog(Array.from(selectedOrphanIds))}
                  data-testid="bulk-archive-open-btn"
                >
                  Archivar
                </Button>
              </div>
            </div>
          )}
          {leads.length === 0 ? (
            <Card className="bg-card/50 backdrop-blur-md border-white/5"><CardContent className="py-16 text-center"><Users className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" /><p className="text-muted-foreground">No hay leads sin asignar por baja de vendedor.</p></CardContent></Card>
          ) : leads.map(lead => {
            const snapshot = lead.vendor_removal_snapshot;
            const checked = selectedOrphanIds.has(lead.id);
            return (
              <Card key={lead.id} className="bg-card/50 backdrop-blur-md border-white/5 hover:border-white/10" data-testid={`orphan-lead-card-${lead.id}`}>
                <CardContent className="p-4 flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={checked}
                    disabled={lead.archived}
                    title={lead.archived ? 'Un lead archivado no se puede reasignar -- desarchivalo primero' : undefined}
                    onChange={() => setSelectedOrphanIds(prev => {
                      const next = new Set(prev);
                      if (next.has(lead.id)) next.delete(lead.id); else next.add(lead.id);
                      return next;
                    })}
                    className="w-4 h-4 shrink-0 disabled:opacity-30 disabled:cursor-not-allowed"
                    data-testid={`orphan-lead-checkbox-${lead.id}`}
                  />
                  <div className="flex-1 min-w-0 cursor-pointer" onClick={() => openDetail(lead.id)}>
                    <p className="font-medium text-white text-sm truncate">{lead.name}</p>
                    <p className="text-[11px] text-muted-foreground truncate">{lead.phone ? comoTelefono(lead.phone) : (lead.email || 'Sin contacto')}</p>
                    {/* leads-sin-asignar-por-baja-vendedor (ampliacion 2026-08-25): esta vista ya
                        trae tanto huerfanos por baja COMO leads que nunca tuvieron vendedor -- sin
                        snapshot, el tag tiene que decir eso, nunca quedar en blanco (confuso: por
                        que unas tarjetas tienen info y otras no). */}
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-400/10 text-amber-300 border border-amber-400/20 inline-block mt-1" data-testid={`orphan-previous-vendor-${lead.id}`}>
                      {snapshot ? `Vendedor que tenía asignado: ${snapshot.previous_vendor_name || 'Vendedor no disponible'}` : 'Nunca tuvo vendedor asignado'}
                    </span>
                    {/* archivar-leads-de-prueba: con "Ver archivados" activo, esta
                        misma lista mezcla leads activos y archivados -- el tag evita
                        confundirlos. */}
                    {lead.archived && (
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-zinc-500/10 text-zinc-400 border border-zinc-500/20 inline-block mt-1 ml-1" data-testid={`orphan-archived-badge-${lead.id}`}>
                        Archivado
                      </span>
                    )}
                  </div>
                  <span className={`px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider shrink-0 ${ratingColorClass(lead.status)}`}>{ratingLabel(lead.status, cemRatings)}</span>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : leads.length === 0 ? (
        <Card className="bg-card/50 backdrop-blur-md border-white/5"><CardContent className="py-16 text-center"><Users className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" /><p className="text-muted-foreground">No hay leads. Crea tu primer lead o configura un webhook.</p></CardContent></Card>
      ) : (
        <div className="space-y-3" ref={listRef}>
          {leads.map(lead => {
            if (isMobile) {
              const isExpanded = expandedLeadIds.has(lead.id);
              const reassignment = _lastReassignment(lead);
              const reactionSec = _reactionSeconds(lead);
              return (
                <Card key={lead.id} className={`bg-card/50 backdrop-blur-md border-white/5 hover:border-white/10 ${isDupLead(lead) ? 'lead-dup-alert' : ''}`} style={{ transition: 'border-color 0.2s ease' }} data-testid={`lead-card-${lead.id}`}>
                  <CardContent className="p-3" onClick={() => toggleLeadExpanded(lead.id)} data-testid={`lead-card-toggle-${lead.id}`}>
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center text-sm font-bold text-primary shrink-0">{lead.name?.charAt(0)?.toUpperCase()}</div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-white text-sm truncate">{lead.name}</p>
                        {isDupLead(lead) && (
                          <button
                            type="button"
                            className="text-[9px] px-1.5 py-0.5 rounded-full bg-amber-400/20 text-amber-200 border border-amber-400/40 inline-flex items-center gap-0.5 mt-0.5"
                            onClick={(e) => openDupFamily(e, lead.id)}
                            data-testid={`lead-dup-badge-mobile-${lead.id}`}
                          >
                            <Repeat className="w-2.5 h-2.5" />Duplicado ×{frequencyOf(lead)}
                          </button>
                        )}
                        <div className="flex items-center gap-1 mt-0.5 text-[11px] text-muted-foreground truncate">
                          {lead.phone ? (<><Phone className="w-3 h-3 shrink-0" /><span className="truncate">{comoTelefono(lead.phone)}</span></>) : lead.bsuid ? (<><Phone className="w-3 h-3 shrink-0" /><span className="truncate">WhatsApp sin número</span></>) : lead.email ? (<><Mail className="w-3 h-3 shrink-0" /><span className="truncate">{lead.email}</span></>) : null}
                        </div>
                      </div>
                      <span className={`px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider shrink-0 ${ratingColorClass(lead.status)}`}>{ratingLabel(lead.status, cemRatings)}</span>
                      {isExpanded ? <ChevronUp className="w-4 h-4 text-muted-foreground shrink-0" /> : <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" />}
                    </div>

                    {isExpanded && (
                      <div className="mt-3 pt-3 border-t border-white/5 space-y-2" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between text-[11px] text-muted-foreground">
                          <span
                            className="cursor-pointer hover:text-white flex items-center gap-1 transition-colors"
                            onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(lead.id); toast.success('ID copiado'); }}
                            title={lead.id}
                            data-testid={`copy-lead-id-${lead.id}`}
                          >
                            {id8(lead.id)} <Copy className="w-2.5 h-2.5" />
                          </span>
                          <span data-testid={`lead-created-${lead.id}`}>{formatCallDate(lead.created_at)}</span>
                        </div>
                        {(() => {
                          const aiCount = (lead.ai_skill_actions || []).reduce((n, a) => n + (a && a.ok ? 1 : 0), 0);
                          return aiCount > 0 ? (
                            <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-sky-500/15 text-sky-300 border border-sky-400/30 inline-flex items-center gap-0.5" data-testid={`lead-ai-actions-badge-${lead.id}`}>
                              <Zap className="w-2.5 h-2.5" />{aiCount} acciones IA
                            </span>
                          ) : null;
                        })()}
                        {lead.ai_score && <span className="text-[11px] text-green-400 flex items-center gap-1"><Sparkles className="w-3 h-3" />Score IA: {lead.ai_score}/10</span>}
                        {(reassignment || reactionSec != null) && (
                          <div className="flex items-center gap-2 flex-wrap">
                            {reassignment && (
                              <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-orange-400/10 text-orange-300 border border-orange-400/20" data-testid={`lead-reassigned-${lead.id}`}>
                                Reasignado: {resolveHistorialVendorName(reassignment.from_vendor, reassignment.from_vendor_name)} → {resolveHistorialVendorName(reassignment.to_vendor, reassignment.to_vendor_name)} ({formatCallDate(reassignment.at)})
                              </span>
                            )}
                            {reactionSec != null && (
                              <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-cyan-400/10 text-cyan-300 border border-cyan-400/20" data-testid={`lead-reaction-time-${lead.id}`}>
                                Reacción del vendedor: {_formatDuration(reactionSec)}
                              </span>
                            )}
                          </div>
                        )}
                        {(allowedModules.developments || allowedModules.products || allowedModules.brands || showOperationType) && (lead.development_id || lead.product_id || lead.lead_source_id || lead.campaign_name || lead.brand_id || lead.operation_type) && (
                          <div className="flex items-center gap-1.5 flex-wrap">
                            {allowedModules.developments && lead.development_id && getDevTitle(lead.development_id) && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-violet-400/10 text-violet-400 border border-violet-400/20" data-testid={`lead-development-${lead.id}`}>{getDevTitle(lead.development_id)}</span>}
                            {allowedModules.brands && lead.brand_id && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-amber-400/10 text-amber-400 border border-amber-400/20" data-testid={`lead-brand-${lead.id}`}>{getBrandName(lead.brand_id)}</span>}
                            {allowedModules.products && lead.product_id && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-emerald-400/10 text-emerald-400 border border-emerald-400/20">{getProductName(lead.product_id)}</span>}
                            {(allowedModules.developments || allowedModules.products) && (lead.lead_source_id || lead.campaign_name) && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-sky-400/10 text-sky-400 border border-sky-400/20">{getSourceName(lead.lead_source_id) || lead.campaign_name}</span>}
                            {showOperationType && lead.operation_type && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-pink-400/10 text-pink-400 border border-pink-400/20" data-testid={`lead-operation-type-${lead.id}`}>{lead.operation_type === 'renta' ? 'Renta' : 'Venta'}</span>}
                          </div>
                        )}
                        {(lead.source === "whatsapp_mass_campaign" || lead.mass_campaign_id) && (
                          <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-purple-400/10 text-purple-400 border border-purple-400/20 inline-flex items-center gap-1" data-testid={`lead-mass-campaign-badge-${lead.id}`}>
                            <span>📣</span>Respondió a masiva{lead.campaign_name && <span className="font-medium">· {lead.campaign_name}</span>}
                          </span>
                        )}
                        {lead.car_model_id && (
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-500/20 text-blue-300 border border-blue-500/30 inline-block" data-testid={`lead-car-model-${lead.id}`}>
                            🚗 {lead.car_model_label || 'Interesado en modelo'}
                          </span>
                        )}
                        {lead.ai_last_summary && <p className="text-[10px] text-fuchsia-200/70 line-clamp-2">{lead.ai_last_summary}</p>}
                        {lead.properties_of_interest?.length > 0 && (
                          <div className="flex flex-wrap gap-1">
                            {lead.properties_of_interest.map(pid => (
                              <span key={pid} className="text-[9px] px-1.5 py-0.5 rounded-full bg-blue-400/10 text-blue-400 border border-blue-400/20 flex items-center gap-1">
                                <Building2 className="w-2.5 h-2.5" />{getPropTitle(pid) || 'Propiedad'}
                              </span>
                            ))}
                          </div>
                        )}
                        <div className="flex items-center gap-2 pt-1 flex-wrap">
                          <span className="text-[10px] bg-muted/50 px-2 py-0.5 rounded-full text-muted-foreground">{lead.source}</span>
                          <Select value={lead.status} onValueChange={(v) => updateStatus(lead, v)}>
                            <SelectTrigger className="h-7 w-auto border-0 bg-black/20 px-2 text-[10px]" data-testid={`lead-status-${lead.id}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {cemRatings.map(r => <SelectItem key={r.id} value={r.id}>{r.label}</SelectItem>)}
                              {lead.status && !cemRatings.some(r => r.id === lead.status) && (
                                <SelectItem value={lead.status}>{ratingLabel(lead.status, cemRatings)}</SelectItem>
                              )}
                            </SelectContent>
                          </Select>
                          <span className="text-[10px] text-muted-foreground">{getVendorName(lead.assigned_to)}</span>
                          <div className="ml-auto flex items-center gap-1">
                            <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); openDetail(lead.id); }} className="h-7 text-[10px] px-2 text-muted-foreground hover:text-white" data-testid={`lead-view-detail-${lead.id}`}>Ver detalle</Button>
                            <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); openEdit(lead); }} className="h-7 w-7 p-0 text-muted-foreground hover:text-[#A5D62C] hover:bg-[#A5D62C]/10" data-testid={`edit-lead-${lead.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                          </div>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            }
            return (
            <Card key={lead.id} className={`bg-card/50 backdrop-blur-md border-white/5 hover:border-white/10 cursor-pointer ${isDupLead(lead) ? 'lead-dup-alert' : ''}`} style={{ transition: 'border-color 0.2s ease' }} data-testid={`lead-card-${lead.id}`} onClick={() => openDetail(lead.id)}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4 flex-1 min-w-0">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-sm font-bold text-primary shrink-0">{lead.name?.charAt(0)?.toUpperCase()}</div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-white text-sm">{lead.name}</p>
                        {isDupLead(lead) && (
                          <button
                            type="button"
                            className="text-[9px] px-1.5 py-0.5 rounded-full bg-amber-400/20 text-amber-200 border border-amber-400/40 flex items-center gap-0.5 shrink-0"
                            onClick={(e) => openDupFamily(e, lead.id)}
                            data-testid={`lead-dup-badge-${lead.id}`}
                            title="Ver consultas previas"
                          >
                            <Repeat className="w-2.5 h-2.5" />
                            Duplicado ×{frequencyOf(lead)}
                          </button>
                        )}
                        {(() => {
                          const aiCount = (lead.ai_skill_actions || []).reduce((n, a) => n + (a && a.ok ? 1 : 0), 0);
                          return aiCount > 0 ? (
                            <span
                              className="text-[9px] px-1.5 py-0.5 rounded-full bg-sky-500/15 text-sky-300 border border-sky-400/30 flex items-center gap-0.5 shrink-0"
                              title={`${aiCount} acción(es) ejecutada(s) por la IA`}
                              data-testid={`lead-ai-actions-badge-${lead.id}`}
                            >
                              <Zap className="w-2.5 h-2.5" />{aiCount} acciones IA
                            </span>
                          ) : null;
                        })()}
                        <span
                          className="text-[8px] text-muted-foreground/50 cursor-pointer hover:text-white flex items-center gap-1 transition-colors"
                          onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(lead.id); toast.success('ID copiado'); }}
                          title={lead.id}
                          data-testid={`copy-lead-id-${lead.id}`}
                        >
                          {id8(lead.id)} <Copy className="w-2.5 h-2.5" />
                        </span>
                      </div>
                      <div className="flex items-center gap-3 mt-0.5">
                        {lead.email && <span className="text-[11px] text-muted-foreground flex items-center gap-1"><Mail className="w-3 h-3" />{lead.email}</span>}
                        {(lead.phone || lead.bsuid) && <span className="text-[11px] text-muted-foreground flex items-center gap-1"><Phone className="w-3 h-3" />{lead.phone ? comoTelefono(lead.phone) : 'WhatsApp sin número'}</span>}
                        {lead.ai_score && <span className="text-[11px] text-green-400 flex items-center gap-1"><Sparkles className="w-3 h-3" />Score IA: {lead.ai_score}/10</span>}
                        <span className="text-[11px] text-muted-foreground flex items-center gap-1" data-testid={`lead-created-${lead.id}`}>{formatCallDate(lead.created_at)}</span>
                      </div>
                      {(() => {
                        const reassignment = _lastReassignment(lead);
                        const reactionSec = _reactionSeconds(lead);
                        if (!reassignment && reactionSec == null) return null;
                        return (
                          <div className="flex items-center gap-3 mt-1 flex-wrap">
                            {reassignment && (
                              <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-orange-400/10 text-orange-300 border border-orange-400/20" data-testid={`lead-reassigned-${lead.id}`}>
                                Reasignado: {resolveHistorialVendorName(reassignment.from_vendor, reassignment.from_vendor_name)} → {resolveHistorialVendorName(reassignment.to_vendor, reassignment.to_vendor_name)} ({formatCallDate(reassignment.at)})
                              </span>
                            )}
                            {reactionSec != null && (
                              <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-cyan-400/10 text-cyan-300 border border-cyan-400/20" data-testid={`lead-reaction-time-${lead.id}`}>
                                Reacción del vendedor: {_formatDuration(reactionSec)}
                              </span>
                            )}
                          </div>
                        );
                      })()}
                      {(allowedModules.developments || allowedModules.products || allowedModules.brands || showOperationType) && (lead.development_id || lead.product_id || lead.lead_source_id || lead.campaign_name || lead.brand_id || lead.operation_type) && (
                        <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                          {allowedModules.developments && lead.development_id && getDevTitle(lead.development_id) && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-violet-400/10 text-violet-400 border border-violet-400/20" data-testid={`lead-development-${lead.id}`}>{getDevTitle(lead.development_id)}</span>}
                          {allowedModules.brands && lead.brand_id && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-amber-400/10 text-amber-400 border border-amber-400/20" data-testid={`lead-brand-${lead.id}`}>{getBrandName(lead.brand_id)}</span>}
                          {allowedModules.products && lead.product_id && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-emerald-400/10 text-emerald-400 border border-emerald-400/20">{getProductName(lead.product_id)}</span>}
                          {(allowedModules.developments || allowedModules.products) && (lead.lead_source_id || lead.campaign_name) && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-sky-400/10 text-sky-400 border border-sky-400/20">{getSourceName(lead.lead_source_id) || lead.campaign_name}</span>}
                          {showOperationType && lead.operation_type && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-pink-400/10 text-pink-400 border border-pink-400/20" data-testid={`lead-operation-type-${lead.id}`}>{lead.operation_type === 'renta' ? 'Renta' : 'Venta'}</span>}
                        </div>
                      )}
                      {(lead.source === "whatsapp_mass_campaign" || lead.mass_campaign_id) && (
                        <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                          <span
                            className="text-[9px] px-1.5 py-0.5 rounded-full bg-purple-400/10 text-purple-400 border border-purple-400/20 flex items-center gap-1"
                            data-testid={`lead-mass-campaign-badge-${lead.id}`}
                          >
                            <span>📣</span>
                            Respondió a masiva
                            {lead.campaign_name && (
                              <span className="font-medium">· {lead.campaign_name}</span>
                            )}
                          </span>
                        </div>
                      )}
                      {lead.car_model_id && (
                        <div className="flex items-center gap-1.5 mt-1">
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-500/20 text-blue-300 border border-blue-500/30" data-testid={`lead-car-model-${lead.id}`}>
                            🚗 {lead.car_model_label || 'Interesado en modelo'}
                          </span>
                        </div>
                      )}
                      {lead.ai_last_summary && <p className="text-[10px] text-fuchsia-200/70 mt-1 line-clamp-2">{lead.ai_last_summary}</p>}
                      {/* Properties of interest */}
                      {lead.properties_of_interest?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1.5">
                          {lead.properties_of_interest.map(pid => (
                            <span key={pid} className="text-[9px] px-1.5 py-0.5 rounded-full bg-blue-400/10 text-blue-400 border border-blue-400/20 flex items-center gap-1">
                              <Building2 className="w-2.5 h-2.5" />{getPropTitle(pid) || 'Propiedad'}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <span className="text-[10px] bg-muted/50 px-2 py-0.5 rounded-full text-muted-foreground">{lead.source}</span>
                      <Select value={lead.status} onValueChange={(v) => updateStatus(lead, v)}>
                        <SelectTrigger className="h-7 w-auto border-0 bg-transparent p-0" data-testid={`lead-status-${lead.id}`}>
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${ratingColorClass(lead.status)}`}>{ratingLabel(lead.status, cemRatings)}</span>
                        </SelectTrigger>
                        <SelectContent>
                          {cemRatings.map(r => <SelectItem key={r.id} value={r.id}>{r.label}</SelectItem>)}
                          {lead.status && !cemRatings.some(r => r.id === lead.status) && (
                            <SelectItem value={lead.status}>{ratingLabel(lead.status, cemRatings)}</SelectItem>
                          )}
                        </SelectContent>
                      </Select>
                      <span className="text-[10px] text-muted-foreground">{getVendorName(lead.assigned_to)}</span>
                      <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); openEdit(lead); }} className="h-7 w-7 p-0 text-muted-foreground hover:text-[#A5D62C] hover:bg-[#A5D62C]/10" data-testid={`edit-lead-${lead.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
            );
          })}
        </div>
      )}

      {/* paginacion-lista-leads: paginador al pie de la tabla -- aplica a las
          3 vistas de arriba (huerfanos/vacio/lista normal), backend pagina
          igual en todas. Se oculta si no hay mas de una pagina (nada que
          navegar). */}
      {total > pageSize && (
        <div className="flex flex-wrap items-center justify-between gap-3 mt-4" data-testid="leads-pagination">
          <p className="text-xs text-muted-foreground">
            Mostrando {Math.min((page - 1) * pageSize + 1, total)}–{Math.min(page * pageSize, total)} de {total}
          </p>
          <div className="flex items-center gap-2">
            <Select value={String(pageSize)} onValueChange={(v) => setPageSize(Number(v))}>
              <SelectTrigger className="h-8 w-[80px] text-xs" data-testid="leads-page-size-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="25">25</SelectItem>
                <SelectItem value="50">50</SelectItem>
                <SelectItem value="100">100</SelectItem>
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              size="sm"
              className="h-8 w-8 p-0"
              disabled={page <= 1}
              onClick={() => setPage(p => Math.max(1, p - 1))}
              data-testid="leads-page-prev"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="text-xs text-muted-foreground px-1">
              Página {page} de {Math.max(1, Math.ceil(total / pageSize))}
            </span>
            <Button
              variant="outline"
              size="sm"
              className="h-8 w-8 p-0"
              disabled={page >= Math.ceil(total / pageSize)}
              onClick={() => setPage(p => p + 1)}
              data-testid="leads-page-next"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Create/Edit Lead Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto bg-[#1F2833] border-white/10">
          <DialogHeader>
            <DialogTitle className="font-heading text-white">{editing ? 'Editar Lead' : 'Nuevo Lead'}</DialogTitle>
            <DialogDescription className="text-muted-foreground">{editing ? 'Modifica la informacion del lead' : 'Captura la informacion del lead'}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/5 px-3 py-2" data-testid="lead-create-required-note"><p className="text-[11px] text-emerald-300">{allowedModules.products ? 'Obligatorios: Nombre y al menos un contacto (teléfono, email o BSUID).' : 'Obligatorios: Nombre, Canal/Origen y al menos un contacto (teléfono, email o BSUID).'}</p></div>
            <div className="space-y-2"><Label className="flex items-center gap-1">Nombre *<HelpTip title="Nombre" content="Nombre completo del lead para seguimiento comercial." /></Label><Input data-testid="lead-name" value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="Nombre completo" className="bg-black/20 border-white/10 focus:border-primary" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label className="flex items-center gap-1">Email *<HelpTip title="Email" content="Necesario para envío de cotizaciones y seguimiento." /></Label><Input data-testid="lead-email" type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="bg-black/20 border-white/10 focus:border-primary" /></div>
              <div className="space-y-2"><Label className="flex items-center gap-1">Telefono<HelpTip title="Teléfono" content="Usado para llamadas, WhatsApp y contacto inmediato." /></Label><PhoneInput data-testid="lead-phone" value={form.phone} onChange={v => setForm({...form, phone: v})} /></div>
              <div className="space-y-2"><Label className="flex items-center gap-1">BSUID (WhatsApp sin número)<HelpTip title="BSUID" content="Identificador de Meta cuando el contacto ocultó su teléfono. Si lo tienes, el lead sigue visible y se le puede escribir por WhatsApp." /></Label><Input data-testid="lead-bsuid" value={form.bsuid || ''} onChange={e => setForm({...form, bsuid: e.target.value})} placeholder="MX.…" className="bg-black/20 border-white/10" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {!allowedModules.products && (
                <div className="space-y-2">
                  <Label className="flex items-center gap-1">Canal / Origen *<HelpTip title="Canal/Origen" content="Crítico para atribución de campañas y performance." /></Label>
                  <Select value={form.source} onValueChange={v => setForm({...form, source: v})}>
                    <SelectTrigger className="bg-black/20 border-white/10" data-testid="lead-source"><SelectValue /></SelectTrigger>
                    <SelectContent>{SOURCES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              <div className={`space-y-2 ${allowedModules.products ? 'col-span-2' : ''}`}>
                <Label>Asignar a</Label>
                <Select value={form.assigned_to} onValueChange={v => setForm({...form, assigned_to: v})}>
                  <SelectTrigger className="bg-black/20 border-white/10" data-testid="lead-assigned"><SelectValue placeholder="Auto (Ruleta)" /></SelectTrigger>
                  {/* leads-sin-asignar-por-baja-vendedor: no ofrecer como destino a un
                      vendedor ya desactivado -- mismo bug que se corrige de paso en el
                      dropdown de reasignacion masiva/individual. */}
                  <SelectContent><SelectItem value="auto">Auto (Ruleta)</SelectItem>{team.filter(m => m.is_active !== false).map(m => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>

            {allowedModules.products && (
              <div className="space-y-2 rounded-lg border border-white/5 bg-black/10 p-3" data-testid="lead-product-campaign-group">
                <Label className="text-xs text-white/60 flex items-center gap-1">Producto y Campana (opcional)<HelpTip title="Producto y Campana" content="Selecciona primero un producto; las campanas se filtraran por ese producto." /></Label>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1">
                    <p className="text-[9px] text-muted-foreground">Paso 1 — Producto</p>
                    <Select
                      value={form.product_id || 'none'}
                      onValueChange={v => {
                        if (!v || v === 'none') {
                          setForm({ ...form, product_id: '', lead_source_id: '', brand_id: '' });
                          return;
                        }
                        const p = products.find(pr => pr.id === v);
                        setForm({ ...form, product_id: v, lead_source_id: '', brand_id: p?.brand_id || '' });
                      }}
                    >
                      <SelectTrigger className="bg-black/20 border-white/10" data-testid="lead-product-select">
                        <SelectValue placeholder="Sin producto" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Sin producto</SelectItem>
                        {products.map(p => (
                          <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <p className="text-[9px] text-muted-foreground">Paso 2 — Campana</p>
                    <Select
                      value={form.lead_source_id || 'none'}
                      disabled={!form.product_id}
                      onValueChange={v => {
                        if (!v || v === 'none') {
                          setForm({ ...form, lead_source_id: '' });
                          return;
                        }
                        const src = leadSources.find(s => s.id === v);
                        setForm({
                          ...form,
                          lead_source_id: v,
                          brand_id: src?.brand_id || form.brand_id || '',
                          product_id: src?.product_id || form.product_id || '',
                        });
                      }}
                    >
                      <SelectTrigger className="bg-black/20 border-white/10 disabled:opacity-50" data-testid="lead-campaign-select">
                        <SelectValue placeholder={form.product_id ? 'Sin campana' : 'Elige un producto primero'} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Sin campana</SelectItem>
                        {leadSources.filter(s => s.product_id === form.product_id).map(s => (
                          <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                {(form.product_id || form.lead_source_id) && (
                  <p className="text-[9px] text-muted-foreground" data-testid="lead-product-campaign-summary">
                    {form.product_id && `Producto: ${products.find(p => p.id === form.product_id)?.name || form.product_id}`}
                    {form.brand_id && ` · Marca: ${brands.find(b => b.id === form.brand_id)?.name || form.brand_id}`}
                    {form.lead_source_id && ` · Campana: ${leadSources.find(s => s.id === form.lead_source_id)?.name || ''}`}
                  </p>
                )}
                {form.product_id && leadSources.filter(s => s.product_id === form.product_id).length === 0 && (
                  <p className="text-[9px] text-amber-400/70">Este producto aun no tiene campanas. Crealas en Productos → Campanas.</p>
                )}
              </div>
            )}

            {/* Desarrollo de Interes - Seleccion UNICA (development_id singular) */}
            {allowedModules.developments && (
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Building className="w-3.5 h-3.5 text-emerald-400" />
                Desarrollo de Interes <span className="text-rose-400">*</span>{form.development_id ? `: ${getDevTitle(form.development_id) || 'Desarrollo'}` : ''}
              </Label>
              <div className="max-h-32 overflow-y-auto rounded-lg border border-white/5 bg-black/20">
                {developments.length === 0 ? (
                  <p className="text-xs text-muted-foreground text-center py-3">No hay desarrollos</p>
                ) : (
                  developments.map(d => (
                    <button
                      key={d.id}
                      type="button"
                      onClick={() => toggleDevelopment(d.id)}
                      className={`w-full text-left px-3 py-2 text-xs border-b border-white/5 last:border-0 transition-colors flex items-center justify-between ${
                        form.development_id === d.id ? 'bg-emerald-400/10 text-emerald-400' : 'text-white/70 hover:bg-white/5'
                      }`}
                      data-testid={`dev-interest-${d.id}`}
                    >
                      <span className="truncate">{d.name || d.title}{d.city ? ` - ${d.city}` : ''}</span>
                      {form.development_id === d.id && <Badge className="text-[8px] h-4 bg-emerald-400/20 text-emerald-400 border-0">Seleccionado</Badge>}
                    </button>
                  ))
                )}
              </div>
              {!editing && !String(form.development_id || '').trim() && (
                <p className="text-[11px] text-rose-300" data-testid="dev-required-hint">Selecciona un desarrollo para continuar.</p>
              )}
            </div>
            )}

            {/* tipo-operacion-renta-venta: opcional, solo desarrollo/inmobiliaria (gate por vertical, no por modulo). */}
            {showOperationType && (
            <div className="space-y-2">
              <Label>Tipo de Operacion</Label>
              <Select
                value={form.operation_type || 'none'}
                onValueChange={(v) => setForm({ ...form, operation_type: v === 'none' ? '' : v })}
              >
                <SelectTrigger className="bg-black/20 border-white/10" data-testid="lead-operation-type-select">
                  <SelectValue placeholder="Sin especificar" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sin especificar</SelectItem>
                  <SelectItem value="renta">Renta</SelectItem>
                  <SelectItem value="venta">Venta</SelectItem>
                </SelectContent>
              </Select>
            </div>
            )}

            {/* Properties of Interest - Multi-select */}
            {allowedModules.properties && (
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Building2 className="w-3.5 h-3.5 text-blue-400" />
                Propiedades de Interes ({form.properties_of_interest?.length || 0})
              </Label>
              {form.properties_of_interest?.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {form.properties_of_interest.map(pid => (
                    <span key={pid} className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-blue-400/10 text-blue-400 border border-blue-400/20">
                      {getPropTitle(pid) || 'Propiedad'}
                      <button onClick={() => toggleProperty(pid)} className="hover:text-destructive"><X className="w-3 h-3" /></button>
                    </span>
                  ))}
                </div>
              )}
              <div className="max-h-32 overflow-y-auto rounded-lg border border-white/5 bg-black/20">
                {properties.length === 0 ? (
                  <p className="text-xs text-muted-foreground text-center py-3">No hay propiedades</p>
                ) : (
                  properties.map(p => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => toggleProperty(p.id)}
                      className={`w-full text-left px-3 py-2 text-xs border-b border-white/5 last:border-0 transition-colors flex items-center justify-between ${
                        form.properties_of_interest?.includes(p.id) ? 'bg-blue-400/10 text-blue-400' : 'text-white/70 hover:bg-white/5'
                      }`}
                      data-testid={`prop-interest-${p.id}`}
                    >
                      <span className="truncate">{p.title} - ${Number(p.price).toLocaleString('es-MX')}</span>
                      {form.properties_of_interest?.includes(p.id) && <Badge className="text-[8px] h-4 bg-blue-400/20 text-blue-400 border-0">Seleccionada</Badge>}
                    </button>
                  ))
                )}
              </div>
            </div>
            )}
            <div className="space-y-2"><Label>Notas</Label><Textarea data-testid="lead-notes" value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} rows={2} className="bg-black/20 border-white/10 focus:border-primary" /></div>
            <Button onClick={saveLead} disabled={!editing && allowedModules.developments && !String(form.development_id || '').trim()} className="w-full bg-primary hover:bg-primary/90 shadow-lg shadow-primary/20 font-semibold disabled:opacity-50 disabled:cursor-not-allowed" data-testid="save-lead-btn">{editing ? 'Actualizar' : 'Crear'} Lead</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto bg-[#1F2833] border-white/10" data-testid="lead-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-white flex items-center gap-2"><FolderKanban className="w-5 h-5 text-fuchsia-400" />Lead 360</DialogTitle>
            <DialogDescription className="text-muted-foreground">Toda la información, seguimiento y actividad del lead en un solo lugar.</DialogDescription>
          </DialogHeader>
          {leadDetail?.lead && (
            <div className="space-y-5">
              <div className="p-4 rounded-xl bg-black/20 border border-white/5">
                <div className="flex items-center justify-between gap-4 flex-wrap">
                  <div>
                    <p className="text-lg font-semibold text-white">{leadDetail.lead.name}</p>
                    <p className="text-xs text-muted-foreground">{leadDetail.lead.email || 'Sin email'} · {comoTelefono(leadDetail.lead.phone) || (leadDetail.lead.bsuid ? 'WhatsApp sin número' : 'Sin teléfono')}</p>
                    {leadDetail?.lead?.notes && (
                      <p className="text-xs text-fuchsia-200/70 mt-2" data-testid="lead-detail-notes">
                        Notas: {leadDetail.lead.notes}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {/* leads-sin-asignar-por-baja-vendedor: mientras el lead siga con el
                        snapshot (nadie lo ha reasignado desde la baja -- se limpia solo,
                        ver core/lead_assignment.py::push_reassignment_history), este bloque
                        reemplaza el badge de Score IA por el aviso + dropdown de reasignacion.
                        Ampliacion 2026-08-25: TAMBIEN aparece sin snapshot cuando el lead
                        directamente no tiene vendedor (nunca tuvo uno) -- mismo widget, mismo
                        lugar, para no perder la comodidad de reasignar desde aca solo porque
                        el lead nunca paso por una baja de vendedor. */}
                    {(leadDetail.lead.vendor_removal_snapshot || !leadDetail.lead.assigned_to) ? (
                      <div className="flex items-center gap-2 flex-wrap" data-testid="lead-detail-vendor-removal">
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-400/10 text-amber-300 border border-amber-400/20">
                          {leadDetail.lead.vendor_removal_snapshot ? (
                            <>
                              Vendedor anterior: {leadDetail.lead.vendor_removal_snapshot.previous_vendor_name || 'Vendedor no disponible'}
                              {' · '}{formatCallDate(leadDetail.lead.vendor_removal_snapshot.removed_at)}
                              {' · dado de baja por '}{leadDetail.lead.vendor_removal_snapshot.removed_by_name || 'un administrador'}
                            </>
                          ) : 'Nunca tuvo vendedor asignado'}
                        </span>
                        <Select value={detailReassignVendor} onValueChange={setDetailReassignVendor}>
                          <SelectTrigger className="h-7 w-[180px] bg-black/20 border-white/10 text-[11px]" data-testid="lead-detail-reassign-select"><SelectValue placeholder="Reasignar a…" /></SelectTrigger>
                          <SelectContent>{team.filter(m => m.is_active !== false).map(m => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
                        </Select>
                        <Button
                          size="sm"
                          onClick={submitDetailReassign}
                          disabled={!detailReassignVendor || detailReassignLoading}
                          className="h-7 text-[10px]"
                          data-testid="lead-detail-reassign-btn"
                        >
                          {detailReassignLoading ? 'Reasignando…' : 'Reasignar'}
                        </Button>
                      </div>
                    ) : (
                      leadDetail.lead.ai_score && <Badge className="bg-fuchsia-500/10 text-fuchsia-300 border-fuchsia-500/20">Score IA: {leadDetail.lead.ai_score}/10</Badge>
                    )}
                    {(leadDetail.lead.source === "whatsapp_mass_campaign" || leadDetail.lead.mass_campaign_id) && (
                      <span
                        className="text-[10px] px-2 py-0.5 rounded-full bg-purple-400/10 text-purple-400 border border-purple-400/20 flex items-center gap-1"
                        data-testid="lead-detail-mass-campaign-badge"
                      >
                        <span>📣</span>
                        Respondió a masiva
                        {leadDetail.lead.campaign_name && (
                          <span className="font-medium">· {leadDetail.lead.campaign_name}</span>
                        )}
                      </span>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      className="border-white/10 text-zinc-300 hover:text-white"
                      onClick={() => setWaLogOpen(true)}
                      data-testid="lead-detail-wa-log-button"
                    >
                      <ClipboardList className="w-3.5 h-3.5 mr-1" />Log WA
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="border-white/10 text-zinc-300 hover:text-white"
                      disabled={debugLeadLoading}
                      onClick={async () => {
                        try {
                          setDebugLeadLoading(true);
                          const r = await api.get(`/leads/${leadDetail.lead.id}/debug`);
                          setDebugLeadJson(r.data);
                          setDebugLeadOpen(true);
                        } catch (e) {
                          toast.error('No se pudo cargar el Debug del lead');
                          console.error(e);
                        } finally {
                          setDebugLeadLoading(false);
                        }
                      }}
                      data-testid="lead-detail-debug-button"
                    >
                      <Bug className="w-3.5 h-3.5 mr-1" />{debugLeadLoading ? 'Cargando…' : 'Debug'}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="border-white/10 text-zinc-300 hover:text-white"
                      onClick={() => setRadioOpen(true)}
                      data-testid="lead-detail-radiografia-button"
                    >
                      <Activity className="w-3.5 h-3.5 mr-1" />Radiografía
                    </Button>
                    {/* archivar-leads-de-prueba (2026-08-26): mismo endpoint que el
                        masivo, lead_ids con 1 solo elemento. */}
                    {leadDetail.lead.archived ? (
                      <Button
                        variant="outline"
                        size="sm"
                        className="border-white/10 text-zinc-300 hover:text-white"
                        disabled={unarchiveLoading}
                        onClick={() => submitUnarchive(leadDetail.lead.id)}
                        data-testid="lead-detail-unarchive-button"
                      >
                        <ArchiveRestore className="w-3.5 h-3.5 mr-1" />{unarchiveLoading ? 'Desarchivando…' : 'Desarchivar'}
                      </Button>
                    ) : (
                      <Button
                        variant="outline"
                        size="sm"
                        className="border-white/10 text-zinc-300 hover:text-white"
                        onClick={() => openArchiveDialog([leadDetail.lead.id])}
                        data-testid="lead-detail-archive-button"
                      >
                        <Archive className="w-3.5 h-3.5 mr-1" />Archivar
                      </Button>
                    )}
                  </div>
                </div>
                {leadDetail.lead.ai_last_summary && <p className="text-xs text-fuchsia-200/70 mt-3">Último resumen IA: {leadDetail.lead.ai_last_summary}</p>}
                {leadDetail?.lead?.car_model_label && (
                  <div className="mt-2 flex items-center gap-2 flex-wrap" data-testid="lead-detail-car-model">
                    <span className="text-xs text-gray-400">Modelo de interés:</span>
                    <span className="text-sm font-medium text-white">{leadDetail.lead.car_model_label}</span>
                    {leadDetail?.lead?.car_model_category && (
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        String(leadDetail.lead.car_model_category).toLowerCase().startsWith('seminuevo')
                          ? 'bg-yellow-500/20 text-yellow-300'
                          : 'bg-green-500/20 text-green-300'
                      }`} data-testid="lead-detail-car-model-category">
                        {leadDetail.lead.car_model_category}
                      </span>
                    )}
                  </div>
                )}
                {allowedModules.developments && leadDetail?.lead?.development_id && getDevTitle(leadDetail.lead.development_id) && (
                  <div className="mt-2 flex items-center gap-2 flex-wrap" data-testid="lead-detail-development">
                    <span className="text-xs text-gray-400">Desarrollo:</span>
                    <span className="text-sm font-medium text-white">{getDevTitle(leadDetail.lead.development_id)}</span>
                  </div>
                )}
                {allowedModules.products && leadDetail?.lead?.product_id && getProductName(leadDetail.lead.product_id) && (
                  <div className="mt-2 flex items-center gap-2 flex-wrap" data-testid="lead-detail-product">
                    <span className="text-xs text-gray-400">Producto:</span>
                    <span className="text-sm font-medium text-white">{getProductName(leadDetail.lead.product_id)}</span>
                  </div>
                )}
              </div>

              {/* Assignment Trace */}
              <AssignmentTraceSection leadId={leadDetail.lead.id} team={team} />

              {/* leads-sin-asignar-por-baja-vendedor: historial de asesores asignados --
                  puramente informativo, sin acciones (la accion de reasignar ya la cubre
                  el badge de vendor_removal_snapshot de arriba). Mas reciente primero --
                  el array se pushea en orden cronologico ascendente. */}
              {leadDetail.lead.reassignment_history?.length > 0 && (
                <Card className="bg-black/20 border-white/5" data-testid="lead-reassignment-history-card">
                  <CardContent className="p-4 space-y-3">
                    <button
                      type="button"
                      onClick={() => setReassignmentHistoryOpen(v => !v)}
                      className="w-full flex items-center justify-between gap-2 text-left"
                      data-testid="lead-reassignment-history-toggle"
                    >
                      <p className="text-xs font-semibold text-white flex items-center gap-2">
                        <RefreshCcw className="w-4 h-4 text-orange-400" />
                        Historial de asesores asignados ({leadDetail.lead.reassignment_history.length})
                      </p>
                      {reassignmentHistoryOpen
                        ? <ChevronUp className="w-4 h-4 text-muted-foreground" />
                        : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
                    </button>
                    {reassignmentHistoryOpen && (
                      <div className="space-y-2 max-h-64 overflow-y-auto">
                        {[...leadDetail.lead.reassignment_history].reverse().map((event, idx) => {
                          const fromName = resolveHistorialVendorName(event.from_vendor, event.from_vendor_name);
                          const toName = resolveHistorialVendorName(event.to_vendor, event.to_vendor_name);
                          return (
                            <div key={idx} className="text-xs text-white/70 border-b border-white/5 pb-2" data-testid={`lead-reassignment-history-item-${idx}`}>
                              <p className="text-[10px] text-muted-foreground">{formatCallDate(event.at)}</p>
                              <p>De: <span className="text-white">{fromName}</span> → A: <span className="text-white">{toName}</span></p>
                              <p className="text-[10px] text-muted-foreground">
                                {getReassignmentSourceLabel(event.source)}{event.actor_name ? ` · por ${event.actor_name}` : ''}
                              </p>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* archivar-leads-de-prueba (2026-08-26): motivo + quien + cuando "que ahi
                  vivan" (Mauricio) -- mismo patron colapsable que el historial de
                  arriba, mas reciente primero (el array se pushea en orden
                  cronologico ascendente). */}
              {leadDetail.lead.archive_history?.length > 0 && (
                <Card className="bg-black/20 border-white/5" data-testid="lead-archive-history-card">
                  <CardContent className="p-4 space-y-3">
                    <button
                      type="button"
                      onClick={() => setArchiveHistoryOpen(v => !v)}
                      className="w-full flex items-center justify-between gap-2 text-left"
                      data-testid="lead-archive-history-toggle"
                    >
                      <p className="text-xs font-semibold text-white flex items-center gap-2">
                        <Archive className="w-4 h-4 text-zinc-400" />
                        Lead archivado ({leadDetail.lead.archive_history.length})
                      </p>
                      {archiveHistoryOpen
                        ? <ChevronUp className="w-4 h-4 text-muted-foreground" />
                        : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
                    </button>
                    {archiveHistoryOpen && (
                      <div className="space-y-2 max-h-64 overflow-y-auto">
                        {[...leadDetail.lead.archive_history].reverse().map((event, idx) => (
                          <div key={idx} className="text-xs text-white/70 border-b border-white/5 pb-2" data-testid={`lead-archive-history-item-${idx}`}>
                            <p className="text-[10px] text-muted-foreground">{formatCallDate(event.at)}</p>
                            <p>{event.action === 'archived' ? 'Archivado' : 'Desarchivado'} por <span className="text-white">{event.actor_name || 'alguien'}</span></p>
                            {event.reason && <p className="text-[10px] text-muted-foreground">Motivo: {event.reason}</p>}
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              <div className="grid md:grid-cols-2 gap-4">
                <Card className="bg-black/20 border-white/5">
                  <CardContent className="p-4 space-y-3">
                    <p className="text-xs font-semibold text-white flex items-center gap-2">
                      <MessageSquare className="w-4 h-4 text-emerald-400" />Conversaciones / consultas
                    </p>
                    {(leadDetail.duplicate_family?.items || []).length > 1 && (
                      <div className="flex gap-1 overflow-x-auto pb-1" data-testid="lead-dup-conv-tabs">
                        {(leadDetail.duplicate_family.items).map((it, idx) => (
                          <button
                            key={it.id}
                            type="button"
                            onClick={() => setConvTabId(it.id)}
                            className={`text-[10px] px-2 py-1 rounded-md border shrink-0 ${
                              (convTabId || leadDetail.lead.id) === it.id
                                ? 'bg-amber-500/20 border-amber-400/40 text-amber-100'
                                : 'bg-white/5 border-white/10 text-muted-foreground'
                            }`}
                          >
                            {it.is_original ? 'Original' : `Consulta ${idx + 1}`}
                          </button>
                        ))}
                      </div>
                    )}
                    <div className="space-y-2 max-h-64 overflow-y-auto" data-testid="lead-conversations">
                      {(() => {
                        const fam = leadDetail.duplicate_family?.items || [];
                        const tab = convTabId || leadDetail.lead.id;
                        const item = fam.find(x => x.id === tab);
                        const msgs = item?.conversation_preview?.length
                          ? item.conversation_preview
                          : (tab === leadDetail.lead.id
                            ? (leadDetail.conversations || []).flatMap(c => c.messages || []).slice(-20)
                            : []);
                        if (!msgs.length) {
                          return <p className="text-[11px] text-muted-foreground">Sin mensajes en esta consulta.</p>;
                        }
                        return [...msgs].reverse().map((msg, idx) => (
                          <div key={idx} className="text-xs text-white/70 border-b border-white/5 pb-2">
                            <p className="text-[10px] text-muted-foreground">{msg.role || msg.direction || ''}</p>
                            <p>{msg.content || msg.body || ''}</p>
                          </div>
                        ));
                      })()}
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-black/20 border-white/5 md:col-span-2" data-testid="lead-calls-unified-card">
                  <CardContent className="p-4 space-y-4">
                    <div className="flex items-center gap-2" data-testid="lead-calls-unified-header">
                      <PhoneCall className="w-5 h-5 text-blue-400" />
                      <h3 className="text-white font-medium">Llamadas</h3>
                    </div>

                    {/* IVR-UI-001: lista única de llamadas (voice_calls), ordenada por created_at desc */}
                    <div className="space-y-2" data-testid="lead-voice-calls-section">
                      <div className="space-y-3 max-h-72 overflow-y-auto" data-testid="lead-voice-calls-list">
                        {(leadDetail.voice_calls || []).length === 0 ? (
                          <div className="text-gray-400 text-sm" data-testid="lead-voice-calls-empty">
                            Sin llamadas registradas
                          </div>
                        ) : (
                          [...leadDetail.voice_calls]
                            .sort((a, b) => {
                              const da = new Date(a.created_at || a.initiated_at || 0).getTime();
                              const db = new Date(b.created_at || b.initiated_at || 0).getTime();
                              return db - da;
                            })
                            .map((call, idx) => (
                              <VoiceCallRow key={call.id || `voice-${idx}`} call={call} idx={idx} />
                            ))
                        )}
                      </div>
                    </div>

                    <Button
                      onClick={() => setActivityDialogOpen(true)}
                      className="w-full bg-emerald-600 hover:bg-emerald-500 text-xs"
                      data-testid="lead-register-activity-button"
                    >
                      <ClipboardList className="w-3.5 h-3.5 mr-1.5" />
                      Registrar Actividad
                    </Button>

                    <Button
                      onClick={() => manualIVRCall(leadDetail.lead?.id)}
                      disabled={manualIvrLoading}
                      className="w-full bg-cyan-600 hover:bg-cyan-500 text-xs"
                      data-testid="lead-manual-ivr-call-button"
                    >
                      {manualIvrLoading ? 'Iniciando llamada...' : '➕ Iniciar Llamada IVR Manual'}
                    </Button>
                  </CardContent>
                </Card>
                <Card className="bg-black/20 border-white/5"><CardContent className="p-4 space-y-3"><p className="text-xs font-semibold text-white flex items-center gap-2"><FolderKanban className="w-4 h-4 text-amber-400" />Oportunidades / seguimiento</p><div className="space-y-2 max-h-64 overflow-y-auto">{(leadDetail.opportunities || []).length === 0 ? <p className="text-xs text-muted-foreground">Sin oportunidades</p> : leadDetail.opportunities.map(opp => <div key={opp.id} className="text-xs text-white/70 border-b border-white/5 pb-2"><p>{opp.title}</p><p className="text-[10px] text-muted-foreground">Etapa: {opp.stage_id} · Asignado: {getVendorName(opp.assigned_to)}</p></div>)}</div></CardContent></Card>
                <Card className="bg-black/20 border-white/5"><CardContent className="p-4 space-y-3"><p className="text-xs font-semibold text-white flex items-center gap-2"><Calendar className="w-4 h-4 text-cyan-400" />Citas / otros registros</p><div className="space-y-2 max-h-64 overflow-y-auto">{(leadDetail.appointments || []).length === 0 ? <p className="text-xs text-muted-foreground">Sin citas registradas</p> : leadDetail.appointments.map(item => <div key={item.id} className="text-xs text-white/70 border-b border-white/5 pb-2"><p>{item.event_name}</p><p className="text-[10px] text-muted-foreground">{item.start_time}</p></div>)}</div></CardContent></Card>
                <Card className="bg-black/20 border-white/5" data-testid="lead-ai-skill-actions-card"><CardContent className="p-4 space-y-3"><p className="text-xs font-semibold text-white flex items-center gap-2"><Sparkles className="w-4 h-4 text-sky-400" />Acciones de IA</p><div className="space-y-2 max-h-64 overflow-y-auto">{(leadDetail.lead?.ai_skill_actions || []).length === 0 ? <p className="text-xs text-muted-foreground" data-testid="lead-ai-skill-actions-empty">Sin acciones de IA registradas</p> : [...(leadDetail.lead.ai_skill_actions || [])].reverse().map((a, idx) => (<div key={`${a.skill}-${idx}`} className="text-xs text-white/70 border-b border-white/5 pb-2 flex items-start justify-between gap-2" data-testid={`lead-ai-skill-action-${idx}`}><div className="min-w-0"><p className="truncate">{a.label || a.skill}{a.value ? <span className="text-sky-300"> · {a.value}</span> : null}</p><p className="text-[10px] text-muted-foreground">{a.campaign_name || 'Campaña IA'} · {(a.created_at || '').slice(0, 16).replace('T', ' ')}</p></div><Badge variant="outline" className={a.ok ? 'border-emerald-500/30 text-emerald-300 text-[9px] shrink-0' : 'border-white/15 text-muted-foreground text-[9px] shrink-0'}>{a.ok ? 'Ejecutada' : 'Pendiente'}</Badge></div>))}</div></CardContent></Card>
                {/* Lead 360, Pieza 2: tarjeta compacta (10 más recientes,
                    misma forma que las demás tarjetas de este modal) + "Ver
                    más" abre el panel completo (B2, ActivitiesBellDialog con
                    leadId fijo). */}
                <Card className="bg-black/20 border-white/5 md:col-span-2" data-testid="lead-activities-compact-card"><CardContent className="p-4 space-y-3"><div className="flex items-center justify-between"><p className="text-xs font-semibold text-white flex items-center gap-2"><Bell className="w-4 h-4 text-amber-400" />Actividades</p><Button variant="ghost" size="sm" onClick={() => setLeadActivitiesViewOpen(true)} className="h-6 text-[10px] text-primary hover:underline px-1" data-testid="lead-activities-view-more">Ver más actividades</Button></div><div className="space-y-2 max-h-64 overflow-y-auto">{(leadDetail.activities || []).length === 0 ? <p className="text-xs text-muted-foreground" data-testid="lead-activities-empty">Sin actividades registradas</p> : leadDetail.activities.map(a => (<div key={a.id} className="text-xs text-white/70 border-b border-white/5 pb-2 flex items-start justify-between gap-2"><div className="min-w-0"><p className="truncate">{a.label || a.type}</p><p className="text-[10px] text-muted-foreground">{formatCallDate(a.created_at)}</p></div>{(a.status === 'pending' || a.status === 'done') && <Badge variant="outline" className={a.status === 'done' ? 'border-emerald-500/30 text-emerald-300 text-[9px] shrink-0' : 'border-amber-500/30 text-amber-300 text-[9px] shrink-0'}>{a.status === 'done' ? 'Realizada' : 'Pendiente'}</Badge>}</div>))}</div></CardContent></Card>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <LeadLogWA
        open={waLogOpen}
        onOpenChange={setWaLogOpen}
        leadId={leadDetail?.lead?.id || ''}
        leadName={leadDetail?.lead?.name || ''}
      />

      {/* leads-sin-asignar-por-baja-vendedor: reasignacion masiva desde la vista de
          huerfanos. reasignacion-pareja-masiva (2026-08-26): multi-select de
          vendedores (antes un solo <Select>) -- elegir 1 = mismo comportamiento de
          siempre; elegir varios = reparto parejo (round-robin), con preview antes
          de confirmar. Reusa la MISMA lista que el selector individual de Lead 360
          (`team.filter(m => m.is_active !== false)`), sin filtro nuevo. */}
      <Dialog open={bulkReassignOpen} onOpenChange={setBulkReassignOpen}>
        <DialogContent className="max-w-md bg-[#1F2833] border-white/10" data-testid="bulk-reassign-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-white">Reasignar leads seleccionados</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              {selectedOrphanIds.size} lead(s) — elegí 1 vendedor para todos, o varios para repartirlos parejo.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5 max-h-48 overflow-y-auto pr-1" data-testid="bulk-reassign-vendor-list">
              {team.filter(m => m.is_active !== false).map(m => {
                const checked = bulkReassignVendorIds.has(m.id);
                return (
                  <label
                    key={m.id}
                    className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-white/5 cursor-pointer text-sm text-white"
                    data-testid={`bulk-reassign-vendor-option-${m.id}`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => setBulkReassignVendorIds(prev => {
                        const next = new Set(prev);
                        if (next.has(m.id)) next.delete(m.id); else next.add(m.id);
                        return next;
                      })}
                      className="w-4 h-4 shrink-0"
                    />
                    {m.name}
                  </label>
                );
              })}
            </div>
            {bulkReassignVendorIds.size > 1 && (
              <div className="text-[11px] text-muted-foreground bg-black/20 border border-white/10 rounded-md p-2" data-testid="bulk-reassign-preview">
                Reparto: {_previewBulkReassignDistribution().map(d => `${d.vendor_name} (${d.count})`).join(' · ')}
              </div>
            )}
            <Button
              onClick={submitBulkReassign}
              disabled={bulkReassignVendorIds.size === 0 || bulkReassignLoading}
              className="w-full bg-primary hover:bg-primary/90 shadow-lg shadow-primary/20 font-semibold disabled:opacity-50"
              data-testid="bulk-reassign-submit-btn"
            >
              {bulkReassignLoading ? 'Reasignando…' : 'Reasignar'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* archivar-leads-de-prueba (2026-08-26): un solo modal, sirve para el
          archivado individual (Lead 360) y el masivo (checkbox de "Sin
          asignar") -- archiveDialogLeadIds trae 1 o varios ids segun de
          donde se abrio. Motivo obligatorio (confirmado por Mauricio). */}
      <Dialog open={archiveDialogOpen} onOpenChange={setArchiveDialogOpen}>
        <DialogContent className="max-w-md bg-[#1F2833] border-white/10" data-testid="archive-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-white">
              {archiveDialogLeadIds.length > 1 ? `Archivar ${archiveDialogLeadIds.length} leads` : 'Archivar lead'}
            </DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Se ocultará de las listas por defecto. Podés desarchivarlo cuando quieras.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Textarea
              value={archiveReason}
              onChange={(e) => setArchiveReason(e.target.value)}
              placeholder="Motivo (obligatorio) — ej. lead de prueba"
              className="bg-black/20 border-white/10 text-sm"
              data-testid="archive-reason-input"
            />
            <Button
              onClick={submitArchive}
              disabled={!archiveReason.trim() || archiveLoading}
              className="w-full bg-primary hover:bg-primary/90 shadow-lg shadow-primary/20 font-semibold disabled:opacity-50"
              data-testid="archive-submit-btn"
            >
              {archiveLoading ? 'Archivando…' : 'Archivar'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <LimitUpgradeDialog
        open={leadLimitDialogOpen}
        onOpenChange={setLeadLimitDialogOpen}
        title="Límite de leads alcanzado"
        description="No puedes crear más leads con este plan. Actualiza o contacta soporte comercial."
        current={total}
        limit={leadLimit < 0 ? 0 : leadLimit}
        testIdPrefix="lead-limit"
      />

      {debugLeadJson?.vista === 'simple' ? (
        <AiHealthSimplePanel
          open={debugLeadOpen}
          onClose={() => setDebugLeadOpen(false)}
          data={debugLeadJson}
        />
      ) : (
        <DebugAssignmentsPanel
          open={debugLeadOpen}
          onClose={() => setDebugLeadOpen(false)}
          debugJson={debugLeadJson}
          title="Debug Info — Lead"
        />
      )}
      <RadiografiaPanel
        leadId={leadDetail?.lead?.id || null}
        open={radioOpen}
        onClose={() => setRadioOpen(false)}
        developmentName={allowedModules.developments && leadDetail?.lead?.development_id ? getDevTitle(leadDetail.lead.development_id) : ''}
      />
      <RegisterActivityDialog
        open={activityDialogOpen}
        onOpenChange={setActivityDialogOpen}
        leadId={leadDetail?.lead?.id || null}
        leadName={leadDetail?.lead?.name || ''}
        onSuccess={() => {
          if (leadDetail?.lead?.id) fetchLeadDetails(leadDetail.lead.id);
        }}
      />
      {/* Lead 360, Pieza 2: mismo patrón que B2 en OpportunitiesPage.js --
          nueva instancia de ActivitiesBellDialog fijada al lead abierto,
          reusando los catálogos team/products que esta página ya carga. */}
      <ActivitiesBellDialog
        open={leadActivitiesViewOpen}
        onOpenChange={setLeadActivitiesViewOpen}
        user={user}
        products={products || []}
        vendors={team || []}
        leads={leads || []}
        leadId={leadDetail?.lead?.id || null}
        leadName={leadDetail?.lead?.name || ''}
      />
      <Dialog open={!!dupFamily} onOpenChange={(o) => { if (!o) { setDupFamily(null); setDupFamilyLeadId(''); } }}>
        <DialogContent className="max-w-lg bg-[#1F2833] border-white/10" data-testid="lead-dup-family-dialog">
          <DialogHeader>
            <DialogTitle className="text-white text-base">Consultas previas</DialogTitle>
            <DialogDescription className="text-xs">
              Mismo contacto. El ID original es el expediente que conserva el vendedor.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {(dupFamily?.items || []).map((it, idx) => (
              <button
                key={it.id}
                type="button"
                onClick={() => { setDupFamily(null); openDetail(it.id); }}
                className="w-full text-left p-3 rounded-lg border border-white/10 hover:border-amber-400/40 bg-black/20"
                data-testid={`lead-dup-family-item-${it.id}`}
              >
                <p className="text-sm text-white">
                  {it.is_original ? 'Original' : `Consulta ${idx + 1}`} · {it.name}
                </p>
                <p className="text-[11px] text-muted-foreground">
                  {it.campaign_name || it.source || 'Sin campaña'} · {formatCallDate(it.created_at)}
                  {it.id === dupFamily?.original_lead_id ? ' · ID original' : ''}
                </p>
              </button>
            ))}
            {!(dupFamily?.items || []).length && (
              <p className="text-xs text-muted-foreground">No hay consultas relacionadas.</p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
